import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, Mail, Shield, FileText, AlertTriangle, Sparkles, Cpu, Lock, 
  Layers, Check, Globe, Send, User, ChevronRight, BookOpen, SendHorizontal,
  Image as ImageIcon, Zap, Search, Eye, Maximize, HelpCircle, LayoutGrid, 
  Star, CreditCard, DollarSign, RefreshCw, IdCard, Home
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';

const getFaqCategory = (item: any) => {
  const question = (item.question || '').toLowerCase();
  if (question.includes('private') || question.includes('security') || question.includes('upload') || question.includes('server') || question.includes('gdpr') || question.includes('cookie') || question.includes('analytics') || question.includes('offline') || question.includes('sandbox')) {
    return 'Privacy & Security';
  }
  if (question.includes('limit') || question.includes('pro') || question.includes('plan') || question.includes('pricing') || question.includes('pay') || question.includes('cost') || question.includes('credit') || question.includes('refund') || question.includes('stripe') || question.includes('billing') || question.includes('subscription') || question.includes('discount')) {
    return 'Plans & Billing';
  }
  if (question.includes('compress') || question.includes('quality') || question.includes('format') || question.includes('bulk') || question.includes('engine') || question.includes('faster') || question.includes('eraser') || question.includes('vector') || question.includes('collage') || question.includes('resizing')) {
    return 'Compression & Engine';
  }
  return 'General & Support';
};

const staticFaqs = [
  // 1. General & Support (10 questions)
  {
    id: 'gen-1',
    question: "What is Smart Imagify and how does it work?",
    answer: "Smart Imagify is an offline graphic studio that works directly inside your browser. You can compress, crop, erase backgrounds, resize, and convert images by dragging your files directly into the workspace. The process is fully secure and doesn't upload files to external servers.",
    category: "General & Support"
  },
  {
    id: 'gen-2',
    question: "Is my data uploaded to any servers during compression?",
    answer: "No. All core processing—including image compression, scaling, formatting, and AI background removal—happens locally inside your browser client. Your private assets never leave your computer.",
    category: "General & Support"
  },
  {
    id: 'gen-3',
    question: "What file formats does Smart Imagify support?",
    answer: "We support JPEGs, PNGs, WebP, AVIF, SVG, and GIF formats. You can optimize, crop, and convert between these files offline in real-time.",
    category: "General & Support"
  },
  {
    id: 'gen-4',
    question: "How do I install Smart Imagify on my desktop?",
    answer: "You can download our dedicated offline client for Windows directly from the user dashboard. macOS and Linux clients are currently in development.",
    category: "General & Support"
  },
  {
    id: 'gen-5',
    question: "Does Smart Imagify work completely offline?",
    answer: "Yes. Once the web application loads, all compression and editing engines run locally. You can disconnect your internet completely and continue working.",
    category: "General & Support"
  },
  {
    id: 'gen-6',
    question: "What makes Smart Imagify different from online compressors?",
    answer: "Unlike standard compressors that require uploading files to a cloud server, Smart Imagify does all the work locally. This eliminates upload time, respects your bandwidth, and guarantees absolute privacy.",
    category: "General & Support"
  },
  {
    id: 'gen-7',
    question: "Is there a limit to the number of images I can compress?",
    answer: "Free users can queue up to 10 files at once for batch operations. Upgrading to a premium account removes batch limits, allowing hundreds of files to process simultaneously.",
    category: "General & Support"
  },
  {
    id: 'gen-8',
    question: "Can I use Smart Imagify for commercial projects?",
    answer: "Absolutely. All images processed and optimized through Smart Imagify can be used freely for commercial, business, marketing, or personal use.",
    category: "General & Support"
  },
  {
    id: 'gen-9',
    question: "How do I contact customer support?",
    answer: "You can email our customer support team directly at support@smartimagify.com or submit a support ticket via the Support tab inside your account dashboard.",
    category: "General & Support"
  },
  {
    id: 'gen-10',
    question: "Are updates to the Smart Imagify offline client free?",
    answer: "Yes, all updates, feature enhancements, and bug fixes for your active tier are automatically applied and free of charge.",
    category: "General & Support"
  },

  // 2. Compression & Engine (10 questions)
  {
    id: 'tool-1',
    question: "Which compression algorithms does Smart Imagify use?",
    answer: "We utilize advanced client-side libraries like MozJPEG for high-quality JPG reduction, pngquant for PNG color quantization, and native codecs for WebP/AVIF generation.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-2',
    question: "What is the difference between Lossy and Lossless compression?",
    answer: "Lossy compression slightly adjusts pixel color values to achieve dramatic file size reductions (often up to 80%) with minimal visible difference. Lossless compression removes redundant code details to shrink files without modifying any pixel values.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-3',
    question: "Can I customize the quality level of compressed images?",
    answer: "Yes. You can adjust quality sliders (from 1% to 100%) to control the balance between image fidelity and file size, with real-time visual previews.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-4',
    question: "Does compressing an image strip its metadata (EXIF/color profiles)?",
    answer: "By default, metadata is stripped to minimize file size. However, you can toggle the 'Preserve Metadata' option in the settings panel to keep color profiles, GPS tags, and camera details.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-5',
    question: "How does the batch compression process queue files?",
    answer: "Smart Imagify uses CPU-based multi-threading to compress multiple images concurrently, maximizing speed and efficiency by utilizing all available cores on your system.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-6',
    question: "Can I convert image formats (e.g., PNG to WebP) during compression?",
    answer: "Yes, you can configure your default output format. You can convert JPEGs, PNGs, and GIFs directly into WebP or AVIF formats during batch compression.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-7',
    question: "What is the maximum file size supported by the engine?",
    answer: "Free tier supports individual file sizes up to 10MB. Premium Creative Pro accounts unlock single-file limits of up to 50MB for ultra-high resolution photography.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-8',
    question: "How does Smart Imagify handle transparency in PNG and WebP files?",
    answer: "Our engine preserves alpha channels during compression. If you convert transparent PNGs to WebP or AVIF, transparency is maintained cleanly without ugly color halos.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-9',
    question: "Can I save custom compression presets for recurring tasks?",
    answer: "Yes, you can configure your favorite quality levels, scale factors, and output formats, then save them as a custom preset for quick one-click optimization.",
    category: "Compression & Engine"
  },
  {
    id: 'tool-10',
    question: "Why are some images compressed more than others?",
    answer: "Compression efficiency depends on image complexity. Smooth gradients and solid colors compress extremely well, while noisy textures and fine details require more bits to maintain clarity.",
    category: "Compression & Engine"
  },

  // 3. Privacy & Security (10 questions)
  {
    id: 'sec-1',
    question: "Are my photos kept private and secure?",
    answer: "Absolutely. Since all compression and rendering engines run directly inside your local web browser sandbox, no external server ever sees, stores, or accesses your graphics.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-2',
    question: "Does Smart Imagify comply with GDPR and CCPA regulations?",
    answer: "Yes. We respect your privacy rights. Since we collect zero image data or telemetry on your private designs, we fully comply with European and California privacy requirements.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-3',
    question: "Do you store metadata or cache compressed images?",
    answer: "No. All cache is temporary in your system memory. Once you download your optimized image or close the tab, all image buffers are completely purged from memory.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-4',
    question: "Does the application collect telemetry or usage statistics?",
    answer: "We only log anonymous app execution status (such as 'successful compression' or 'conversion completed') to fix bugs. We never collect filenames, color values, or personal metadata.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-5',
    question: "Is an active internet connection required to activate the license?",
    answer: "A brief internet connection is required only once to authenticate your account and fetch your profile. Once validated, you can operate completely offline.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-6',
    question: "How are license keys and user credentials secured?",
    answer: "All authentication is handled through Supabase Auth using encrypted JSON Web Tokens (JWT) and SSL transport layers, safeguarding your credentials securely.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-7',
    question: "Does Smart Imagify use local browser storage?",
    answer: "Yes. We store minimal functional keys—such as your light/dark theme preference and dashboard layout config—locally using standard localStorage.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-8',
    question: "Are there any third-party scripts loaded in the offline client?",
    answer: "No. The offline desktop client is isolated and does not log external tracking codes, ads, or third-party analytic pixels.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-9',
    question: "Is the application code audited for security vulnerabilities?",
    answer: "Yes. Our client applications and authentication APIs are regularly audited and updated to patch dependencies and maintain robust client-side security.",
    category: "Privacy & Security"
  },
  {
    id: 'sec-10',
    question: "Can I use Smart Imagify in strict corporate offline environments?",
    answer: "Yes. Because Smart Imagify is entirely client-side, it is highly suitable for corporate networks, government institutions, and legal firms requiring absolute confidentiality.",
    category: "Privacy & Security"
  },

  // 4. Plans & Billing (10 questions)
  {
    id: 'plan-1',
    question: "What is included in the Smart Imagify lifetime plan?",
    answer: "The lifetime plan offers access to all offline compression, conversion, and resizing engines without recurring fees, alongside priority customer support.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-2',
    question: "Is there a free trial or free tier available?",
    answer: "Yes. Our standard offline tools are free to use. Premium subscriptions are required only to increase file size thresholds or run AI background erasers.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-3',
    question: "What payment methods do you support?",
    answer: "We support all major international credit cards, debit cards, Apple Pay, and Google Pay. Payments are processed securely via Stripe.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-4',
    question: "How do I cancel my subscription or request a refund?",
    answer: "You can cancel your subscription inside your billing dashboard at any time. We also offer a 14-day refund guarantee if premium credits haven't been consumed.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-5',
    question: "Can I use my license key on multiple devices?",
    answer: "Yes. Pro user license keys can be activated concurrently on up to 3 personal or business computers under the same user account.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-6',
    question: "Are there any hidden fees or volume-based surcharges?",
    answer: "No. We believe in clear pricing. There are no hidden fees, volume surcharges, or processing taxes added to your selected subscription tier.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-7',
    question: "How do I upgrade or downgrade my current plan?",
    answer: "You can transition between billing tiers from your account settings panel. Downgrades take effect at the end of the billing cycle, while upgrades apply immediately.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-8',
    question: "Will I receive a formal tax invoice for my purchase?",
    answer: "Yes. A PDF receipt and a standard tax invoice are automatically generated and emailed to your billing address after every transaction.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-9',
    question: "What happens if my subscription lapses?",
    answer: "If your subscription expires or lapses, your account will revert to the standard free tier, but your local settings and workspace data will remain intact.",
    category: "Plans & Billing"
  },
  {
    id: 'plan-10',
    question: "Do you offer special discounts for students or non-profits?",
    answer: "Yes. We offer an academic and non-profit discount of 50% on all plans. Reach out to support@smartimagify.com with your credentials to apply.",
    category: "Plans & Billing"
  }
];

interface SpecialPagesProps {
  path: string;
  onBackToHome: () => void;
  addToast: (type: 'success' | 'info' | 'warning' | 'error', text: string) => void;
  onSignIn?: (targetPath?: string, mode?: 'signin' | 'signup') => void;
  onLaunchTool?: () => void;
  onPlanSelect?: (planName: string) => void;
  theme?: 'light' | 'dark';
}

const renderFormattedTitle = (title: string, theme: 'light' | 'dark') => {
  if (!title) return null;
  
  // Support breaking lines with custom line breaks
  const parts = title.split(/\n|\\n|\|/);
  if (parts.length > 1) {
    return (
      <>
        <span className="font-extrabold">{parts[0]}</span> <br />
        <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>
          {parts.slice(1).join(' ')}
        </span>
      </>
    );
  }
  
  // Otherwise, highlight the last 1 or 2 words depending on length
  const words = title.split(' ');
  if (words.length > 2) {
    const mainPart = words.slice(0, words.length - 2).join(' ');
    const highlightPart = words.slice(words.length - 2).join(' ');
    return (
      <>
        <span className="font-extrabold">{mainPart}</span>{' '}
        <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>
          {highlightPart}
        </span>
      </>
    );
  } else if (words.length === 2) {
    return (
      <>
        <span className="font-extrabold">{words[0]}</span>{' '}
        <span className={`font-light ${theme === 'light' ? 'text-zinc-400' : 'text-[#999999]'}`}>
          {words[1]}
        </span>
      </>
    );
  }
  return <span className="font-extrabold">{title}</span>;
};

interface PageWrapperProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  theme: 'light' | 'dark';
  mainPaddingTop: number;
  onBackToHome: () => void;
}

const PageWrapper = ({ children, title, subtitle, theme, mainPaddingTop, onBackToHome }: PageWrapperProps) => {
  const textColor = theme === 'light' ? '#555555' : '#999999';
  return (
    <div className={`w-full font-sans transition-colors duration-300 ${
      theme === 'light' ? 'text-zinc-900 bg-white' : 'text-slate-100 bg-[#050507]'
    }`}>
      <style dangerouslySetInnerHTML={{ __html: `
        .special-pages-policy-content p,
        .special-pages-policy-content span,
        .special-pages-policy-content li {
          color: ${textColor} !important;
        }
        .special-pages-policy-content strong,
        .special-pages-policy-content h1,
        .special-pages-policy-content h2,
        .special-pages-policy-content h3,
        .special-pages-policy-content h4,
        .special-pages-policy-content h5,
        .special-pages-policy-content h6 {
          color: ${theme === 'light' ? '#090b11' : '#ffffff'} !important;
        }
        .special-pages-hero-banner h1 {
          color: #ffffff !important;
        }
        .special-pages-hero-banner h1 span.font-extrabold {
          color: #ffffff !important;
        }
        .special-pages-hero-banner h1 span.font-light {
          color: #999999 !important;
        }
        .special-pages-hero-banner p.subtitle {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .special-pages-hero-banner .breadcrumb-btn {
          color: rgba(255, 255, 255, 0.55) !important;
        }
        .special-pages-hero-banner .breadcrumb-btn:hover {
          color: #ffffff !important;
        }
        .special-pages-hero-banner .breadcrumb-sep {
          color: rgba(255, 255, 255, 0.3) !important;
        }
        .special-pages-hero-banner .breadcrumb-cur {
          color: rgba(255, 255, 255, 0.8) !important;
        }
      ` }} />
      {/* FULL-WIDTH DYNAMIC HERO BANNER */}
      <div 
        className="special-pages-hero-banner relative w-full overflow-hidden" 
        style={{ 
          backgroundColor: '#050507',
          marginTop: `-${mainPaddingTop}px`,
          paddingTop: `${mainPaddingTop}px`,
          minHeight: '380px'
        }}
      >
        {/* Green abstract background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-300"
          style={{ 
            backgroundImage: "url('/assets/green_abstract_bg.png')",
            filter: 'blur(8px)',
            transform: 'scale(1.05)',
            opacity: 1
          }}
        />
        {/* Subtle green overlay light glow */}
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.85) 100%)' 
          }} 
        />
        <div 
          className="absolute inset-0 pointer-events-none" 
          style={{ 
            backgroundColor: 'rgba(170, 206, 22, 0.04)' 
          }} 
        />
        <div 
          className="absolute inset-0 pointer-events-none" 
          style={{ 
            background: 'radial-gradient(ellipse at 50% 50%, rgba(170,206,22,0.06) 0%, transparent 80%)' 
          }} 
        />

        <div className="relative z-10 max-w-[1260px] mx-auto w-full px-[15px] sm:px-[20px] lg:px-[25px] flex flex-col justify-end h-full animate-fade-in" style={{ minHeight: '284px', paddingBottom: '30px' }}>
          <h1 className="text-[36px] sm:text-[48px] md:text-[56px] lg:text-[64px] font-bold tracking-tight leading-[1.1] mb-[7px]">
            {renderFormattedTitle(title, 'dark')}
          </h1>
          {subtitle && (
            <p className="subtitle text-[14px] font-normal mb-[15px]">
              {subtitle}
            </p>
          )}
          {/* Breadcrumb */}
          <div className="flex items-center gap-[7px]">
            <button onClick={onBackToHome} className="breadcrumb-btn text-[11px] font-medium transition-colors duration-[250ms] cursor-pointer select-none">
              Home
            </button>
            <ChevronRight className="breadcrumb-sep w-[10px] h-[10px]" />
            <span className="breadcrumb-cur text-[11px] font-medium">{title}</span>
          </div>
        </div>
      </div>

      {/* 2. CONTENT SECTION - Width matches the website's footer (max-w-7xl), beautifully padded, no heavy borders/colors */}
      <div className={`w-full py-12 md:py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        theme === 'light' ? 'bg-white' : 'bg-[#050507]'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className={`w-full text-left leading-relaxed text-sm md:text-base space-y-8 transition-colors ${
            theme === 'light' ? 'text-zinc-600' : 'text-slate-300'
          }`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SpecialPages({ 
  path, 
  onBackToHome, 
  addToast,
  onSignIn,
  onLaunchTool,
  onPlanSelect,
  theme = 'dark'
}: SpecialPagesProps) {
  const [mainPaddingTop, setMainPaddingTop] = useState(96);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [packages, setPackages] = useState<any[]>([]);
  const [activePricingFaq, setActivePricingFaq] = useState<number | null>(null);

  useEffect(() => {
    if (path !== '/pricing') return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('packages')
          .select('*')
          .order('sort_order', { ascending: true });
          
        if (!error && data && data.length > 0) {
          setPackages(data.filter((p: any) => p.is_active));
        }
      } catch (err) {
        console.warn("Error fetching packages in background:", err);
      }
    })();
  }, [path]);

  useEffect(() => {
    const updatePadding = () => {
      const mainEl = document.querySelector('main');
      if (mainEl) {
        const computedStyle = window.getComputedStyle(mainEl);
        const paddingTopVal = parseInt(computedStyle.paddingTop, 10);
        if (!isNaN(paddingTopVal)) {
          setMainPaddingTop(paddingTopVal);
        }
      }
    };
    updatePadding();
    window.addEventListener('resize', updatePadding);
    const observer = new MutationObserver(updatePadding);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      window.removeEventListener('resize', updatePadding);
      observer.disconnect();
    };
  }, []);

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [faqOpen, setFaqOpen] = useState<number | null>(0);

  // --- FAQ State ---
  const [dbFaqs, setDbFaqs] = useState<any[]>([]);
  const [faqSearchQuery, setFaqSearchQuery] = useState('');
  const [activeFaqId, setActiveFaqId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // --- Tools State ---
  const [toolsSearchQuery, setToolsSearchQuery] = useState('');
  const [toolsActiveTab, setToolsActiveTab] = useState('all');

  // --- Dashboard Preview State ---
  const [credits, setCredits] = useState(950);
  const [simulateActive, setSimulateActive] = useState(false);
  const [simulateProgress, setSimulateProgress] = useState(0);
  const [simulatedSize, setSimulatedSize] = useState<string | null>(null);
  const [simulatedTime, setSimulatedTime] = useState<string | null>(null);
  const [simulateMode, setSimulateMode] = useState<'compress' | 'ai'>('compress');
  const [aiPrompt, setAiPrompt] = useState('A cinematic hyperrealistic render of futuristic neural server structures, black glowing grids, octane render 8k');
  const [aiLogs, setAiLogs] = useState<string[]>([]);
  const [aiArtwork, setAiArtwork] = useState<string | null>(null);

  // --- FAQ Effects ---
  useEffect(() => {
    if (path !== '/faq') return;
    const fetchFaqs = async () => {
      try {
        const { data, error } = await supabase
          .from('faqs')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });
        
        if (!error && data && data.length > 0) {
          setDbFaqs(data);
        }
      } catch (err) {
        console.error("Failed to fetch FAQs:", err);
      }
    };
    fetchFaqs();
  }, [path]);

  const dbFaqParsed = dbFaqs.map(item => {
    const validCategories = ['General & Support', 'Compression & Engine', 'Privacy & Security', 'Plans & Billing'];
    return {
      id: item.id || Math.random().toString(),
      question: item.question,
      answer: item.answer,
      category: validCategories.includes(item.category) ? item.category : getFaqCategory(item)
    };
  });

  const faqList = [...staticFaqs];
  dbFaqParsed.forEach(dbItem => {
    const index = faqList.findIndex(
      sItem => sItem.question.toLowerCase().trim() === dbItem.question.toLowerCase().trim()
    );
    if (index !== -1) {
      faqList[index] = dbItem;
    } else {
      faqList.push(dbItem);
    }
  });

  useEffect(() => {
    if (path !== '/faq') return;
    if (faqList.length > 0) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqList.slice(0, 24).map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      };
      const scriptId = 'faq-jsonld-schema';
      let script = document.getElementById(scriptId) as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.text = JSON.stringify(schema);
      return () => {
        const existing = document.getElementById(scriptId);
        if (existing) existing.remove();
      };
    }
  }, [path, faqList]);

  const triggerRoute = (url: string) => {
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitted(true);
    addToast('success', `Success! We will notify ${email} at launch! 🚀`);
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) return;
    setContactSubmitted(true);
    addToast('success', `Thank you, ${contactName}. Your support ticket has been logged locally! ✉️`);
  };



  // Helper to render standard layout wrappers
  const PageWrapperLegacy = ({ children, title, subtitle }: { children: React.ReactNode, title: string, subtitle?: string }) => {
    const textColor = theme === 'light' ? '#555555' : '#999999';
    return (
      <div className={`w-full font-sans transition-colors duration-300 ${
        theme === 'light' ? 'text-zinc-900 bg-white' : 'text-slate-100 bg-[#050507]'
      }`}>
        <style dangerouslySetInnerHTML={{ __html: `
          .special-pages-policy-content p,
          .special-pages-policy-content span,
          .special-pages-policy-content li {
            color: ${textColor} !important;
          }
          .special-pages-policy-content strong,
          .special-pages-policy-content h1,
          .special-pages-policy-content h2,
          .special-pages-policy-content h3,
          .special-pages-policy-content h4,
          .special-pages-policy-content h5,
          .special-pages-policy-content h6 {
            color: ${theme === 'light' ? '#090b11' : '#ffffff'} !important;
          }
          .special-pages-hero-banner h1 {
            color: #ffffff !important;
          }
          .special-pages-hero-banner h1 span.font-extrabold {
            color: #ffffff !important;
          }
          .special-pages-hero-banner h1 span.font-light {
            color: #999999 !important;
          }
          .special-pages-hero-banner p.subtitle {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          .special-pages-hero-banner .breadcrumb-btn {
            color: rgba(255, 255, 255, 0.55) !important;
          }
          .special-pages-hero-banner .breadcrumb-btn:hover {
            color: #ffffff !important;
          }
          .special-pages-hero-banner .breadcrumb-sep {
            color: rgba(255, 255, 255, 0.3) !important;
          }
          .special-pages-hero-banner .breadcrumb-cur {
            color: rgba(255, 255, 255, 0.8) !important;
          }
        ` }} />
      {/* FULL-WIDTH DYNAMIC HERO BANNER */}
      <div 
        className="special-pages-hero-banner relative w-full overflow-hidden" 
        style={{ 
          backgroundColor: '#050507',
          marginTop: `-${mainPaddingTop}px`,
          paddingTop: `${mainPaddingTop}px`,
          minHeight: '380px'
        }}
      >
        {/* Green abstract background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-300"
          style={{ 
            backgroundImage: "url('/assets/green_abstract_bg.png')",
            filter: 'blur(8px)',
            transform: 'scale(1.05)',
            opacity: 1
          }}
        />
        {/* Subtle green overlay light glow */}
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.85) 100%)' 
          }} 
        />
        <div 
          className="absolute inset-0 pointer-events-none" 
          style={{ 
            backgroundColor: 'rgba(170, 206, 22, 0.04)' 
          }} 
        />
        <div 
          className="absolute inset-0 pointer-events-none" 
          style={{ 
            background: 'radial-gradient(ellipse at 50% 50%, rgba(170,206,22,0.06) 0%, transparent 80%)' 
          }} 
        />

        <div className="relative z-10 max-w-[1260px] mx-auto w-full px-[15px] sm:px-[20px] lg:px-[25px] flex flex-col justify-end h-full animate-fade-in" style={{ minHeight: '284px', paddingBottom: '30px' }}>
          <h1 className="text-[36px] sm:text-[48px] md:text-[56px] lg:text-[64px] font-bold tracking-tight leading-[1.1] mb-[7px]">
            {renderFormattedTitle(title, 'dark')}
          </h1>
          {subtitle && (
            <p className="subtitle text-[14px] font-normal mb-[15px]">
              {subtitle}
            </p>
          )}
          {/* Breadcrumb */}
          <div className="flex items-center gap-[7px]">
            <button onClick={onBackToHome} className="breadcrumb-btn text-[11px] font-medium transition-colors duration-[250ms] cursor-pointer select-none">
              Home
            </button>
            <ChevronRight className="breadcrumb-sep w-[10px] h-[10px]" />
            <span className="breadcrumb-cur text-[11px] font-medium">{title}</span>
          </div>
        </div>
      </div>

      {/* 2. CONTENT SECTION - Width matches the website's footer (max-w-7xl), beautifully padded, no heavy borders/colors */}
      <div className={`w-full py-12 md:py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        theme === 'light' ? 'bg-white' : 'bg-[#050507]'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className={`w-full text-left leading-relaxed text-sm md:text-base space-y-8 transition-colors ${
            theme === 'light' ? 'text-zinc-600' : 'text-slate-300'
          }`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

  // Active Tool Landing Page Wrapper
  const ActiveToolLandingWrapper = ({
    title,
    subtitle,
    description,
    features,
    icon: IconComponent,
    toolPath,
    mockupType
  }: {
    title: string;
    subtitle: string;
    description: string;
    features: string[];
    icon: React.ComponentType<any>;
    toolPath: string;
    mockupType: 'compress' | 'bulk' | 'resize' | 'convert' | 'collage' | 'ai' | 'stock' | 'passport';
  }) => {
    return (
      <div className={`${theme === 'light' ? 'text-zinc-800' : 'text-slate-100'} px-4 sm:px-6 lg:px-8 relative font-sans w-full max-w-7xl mx-auto space-y-20 py-6`}>
        {/* Glow Effects */}
        <div className={`absolute top-1/4 left-1/4 -translate-y-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none -z-10 animate-pulse ${
          theme === 'light' ? 'bg-zinc-200/30' : 'bg-[#aace16]/5'
        }`} />
        <div className={`absolute top-1/3 right-1/4 -translate-y-1/2 translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none -z-10 ${
          theme === 'light' ? 'bg-emerald-500/2' : 'bg-emerald-500/5'
        }`} />

        {/* Hero Section Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Side Copy */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#aace16]/10 border border-[#aace16]/20 rounded-full text-xs font-black text-[#aace16] uppercase tracking-wider">
              <IconComponent className="w-4 h-4 text-[#aace16]" />
              <span>{subtitle}</span>
            </div>
            
            <h1 className={`text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none ${
              theme === 'light' ? 'text-zinc-950' : 'text-white'
            }`}>
              {renderFormattedTitle(title, theme)}
            </h1>
            
            <p className={`text-xs md:text-sm font-semibold leading-relaxed ${
              theme === 'light' ? 'text-zinc-500' : 'text-slate-400'
            }`}>
              {description}
            </p>

            <ul className={`grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-bold ${
              theme === 'light' ? 'text-zinc-700' : 'text-slate-300'
            }`}>
              {features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#aace16]/10 border border-[#aace16]/20 flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#aace16]" />
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <button 
                onClick={onLaunchTool}
                className="px-8 py-4 rounded-2xl bg-[#aace16] text-black text-xs font-black uppercase tracking-wider hover:bg-[#bdef2b] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#aace16]/10 hover:shadow-[#aace16]/25 cursor-pointer"
              >
                <span>Launch in Studio</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => onSignIn?.(toolPath, 'signup')}
                className={`px-6 py-4 rounded-2xl border text-xs font-extrabold uppercase tracking-widest transition-all cursor-pointer text-center ${
                  theme === 'light'
                    ? 'border-zinc-250 bg-white hover:bg-zinc-50 text-zinc-900 shadow-sm'
                    : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 text-white'
                }`}
              >
                Create Free Account
              </button>
            </div>
          </div>

          {/* Right Side UI Mockup Card */}
          <div className="lg:col-span-6">
            <div 
              onClick={onLaunchTool}
              className={`group border rounded-[2.5rem] p-6 space-y-6 relative overflow-hidden transition-all duration-500 hover:border-[#aace16]/30 hover:shadow-[#aace16]/5 cursor-pointer ${
                theme === 'light'
                  ? 'border-zinc-200 bg-white shadow-xl shadow-zinc-200/30'
                  : 'border-white/10 bg-[#121214]/60 backdrop-blur-md shadow-2xl'
              }`}
            >
              {/* Decorative Header buttons */}
              <div className={`flex items-center justify-between border-b pb-4 ${
                theme === 'light' ? 'border-zinc-100' : 'border-white/5'
              }`}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-500/40" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500/40" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/40" />
                </div>
                <div className={`text-[10px] font-mono font-bold px-3 py-1 rounded-full uppercase tracking-wider ${
                  theme === 'light' ? 'text-zinc-400 bg-zinc-100' : 'text-slate-500 bg-white/5'
                }`}>
                  Smart Imagify Sandbox Engine
                </div>
              </div>

              {/* Dynamic Mockup Body depending on tool */}
              {mockupType === 'compress' && (
                <div className="space-y-6 py-4">
                  <div className={`border border-dashed rounded-2xl p-8 text-center transition-colors relative ${
                    theme === 'light' ? 'border-zinc-200 bg-zinc-50/50 hover:bg-zinc-50' : 'border-white/10 bg-white/[0.01] hover:bg-white/[0.02]'
                  }`}>
                    <div className="w-12 h-12 rounded-2xl bg-[#aace16]/10 border border-[#aace16]/20 flex items-center justify-center mx-auto mb-4 text-[#aace16]">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs font-bold block mb-1 ${theme === 'light' ? 'text-zinc-800' : 'text-slate-300'}`}>Drag and Drop Image Here</span>
                    <span className={`text-[10px] font-medium ${theme === 'light' ? 'text-zinc-400' : 'text-slate-500'}`}>Supports JPG, PNG, WebP, AVIF up to 50MB</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-xs font-bold">
                      <span className={theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}>Compression Quality</span>
                      <span className="text-[#aace16] font-mono">82%</span>
                    </div>
                    <div className={`h-1.5 rounded-full overflow-hidden ${theme === 'light' ? 'bg-zinc-100' : 'bg-white/5'}`}>
                      <div className="h-full bg-[#aace16] w-[82%]" />
                    </div>
                  </div>
                </div>
              )}

              {mockupType === 'bulk' && (
                <div className="space-y-6 py-4">
                  <div className={`border border-dashed rounded-2xl p-8 text-center transition-colors relative ${
                    theme === 'light' ? 'border-zinc-200 bg-zinc-50/50 hover:bg-zinc-50' : 'border-white/10 bg-white/[0.01] hover:bg-white/[0.02]'
                  }`}>
                    <div className="w-12 h-12 rounded-2xl bg-[#aace16]/10 border border-[#aace16]/20 flex items-center justify-center mx-auto mb-4 text-[#aace16]">
                      <Layers className="w-6 h-6" />
                    </div>
                    <span className={`text-xs font-bold block mb-1 ${theme === 'light' ? 'text-zinc-800' : 'text-slate-300'}`}>Upload Multiple Images (ZIP/Batch)</span>
                    <span className={`text-[10px] font-medium ${theme === 'light' ? 'text-zinc-400' : 'text-slate-500'}`}>Process up to 200 files simultaneously in parallel</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 font-mono text-[9px] font-bold text-slate-400">
                    <div className={`p-2.5 rounded-lg flex justify-between ${theme === 'light' ? 'bg-zinc-50 border border-zinc-150' : 'bg-white/5'}`}>
                      <span className={theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}>Queue</span>
                      <span className={theme === 'light' ? 'text-zinc-900' : 'text-white'}>12 Files</span>
                    </div>
                    <div className={`p-2.5 rounded-lg flex justify-between ${theme === 'light' ? 'bg-zinc-50 border border-zinc-150' : 'bg-white/5'}`}>
                      <span className={theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}>Thread pool</span>
                      <span className={theme === 'light' ? 'text-zinc-900' : 'text-white'}>WebWorker 4x</span>
                    </div>
                  </div>
                </div>
              )}

              {mockupType === 'resize' && (
                <div className="space-y-6 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className={`text-[10px] font-bold uppercase tracking-wider ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Target Width</label>
                      <div className={`px-3.5 py-2.5 border rounded-xl text-xs font-mono font-bold flex justify-between items-center ${
                        theme === 'light' ? 'bg-zinc-50 border-zinc-250 text-zinc-900' : 'bg-black/40 border border-white/10 text-white'
                      }`}>
                        <span>1920</span>
                        <span className={theme === 'light' ? 'text-zinc-400' : 'text-slate-600'}>PX</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className={`text-[10px] font-bold uppercase tracking-wider ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Target Height</label>
                      <div className={`px-3.5 py-2.5 border rounded-xl text-xs font-mono font-bold flex justify-between items-center ${
                        theme === 'light' ? 'bg-zinc-50 border-zinc-250 text-zinc-900' : 'bg-black/40 border border-white/10 text-white'
                      }`}>
                        <span>1080</span>
                        <span className={theme === 'light' ? 'text-zinc-400' : 'text-slate-600'}>PX</span>
                      </div>
                    </div>
                  </div>
                  <div className={`p-4 rounded-xl flex items-center justify-between text-xs font-semibold ${
                    theme === 'light' ? 'bg-zinc-50 border border-zinc-200' : 'bg-white/[0.01] border border-white/5'
                  }`}>
                    <span className={theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}>Lock Aspect Ratio</span>
                    <div className="w-8 h-4 rounded-full bg-[#aace16] p-0.5 flex justify-end">
                      <div className="w-3 h-3 rounded-full bg-black" />
                    </div>
                  </div>
                </div>
              )}

              {mockupType === 'convert' && (
                <div className="space-y-6 py-4">
                  <div className={`p-4 rounded-2xl space-y-4 ${
                    theme === 'light' ? 'bg-zinc-50 border border-zinc-200' : 'bg-white/[0.01] border border-white/5'
                  }`}>
                    <span className={`text-[10px] font-bold uppercase tracking-wider block ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Export Format Target</span>
                    <div className="grid grid-cols-4 gap-2 font-bold text-[10px] text-center">
                      <div className={`py-2.5 rounded-lg border border-[#aace16] bg-[#aace16]/10 ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>WEBP</div>
                      <div className={`py-2.5 rounded-lg border ${theme === 'light' ? 'border-zinc-200 bg-zinc-200 text-zinc-650' : 'border-white/5 bg-white/5 text-slate-400'}`}>AVIF</div>
                      <div className={`py-2.5 rounded-lg border ${theme === 'light' ? 'border-zinc-200 bg-zinc-200 text-zinc-650' : 'border-white/5 bg-white/5 text-slate-400'}`}>PNG</div>
                      <div className={`py-2.5 rounded-lg border ${theme === 'light' ? 'border-zinc-200 bg-zinc-200 text-zinc-650' : 'border-white/5 bg-white/5 text-slate-400'}`}>JPG</div>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between font-mono text-[9px] font-bold p-2.5 rounded-lg ${
                    theme === 'light' ? 'bg-zinc-100 text-zinc-500' : 'bg-black/20 text-slate-500'
                  }`}>
                    <span>Converting transparent files</span>
                    <span className="text-[#aace16]">Preserving Alpha Channel</span>
                  </div>
                </div>
              )}

              {mockupType === 'collage' && (
                <div className="space-y-6 py-4">
                  <div className="grid grid-cols-3 gap-2">
                    <div className={`aspect-square rounded-xl border flex items-center justify-center font-bold text-lg ${
                      theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-400' : 'bg-white/5 border border-white/10 text-slate-600'
                    }`}>+</div>
                    <div className={`aspect-square rounded-xl border flex items-center justify-center font-bold text-lg ${
                      theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-400' : 'bg-white/5 border border-white/10 text-slate-600'
                    }`}>+</div>
                    <div className={`aspect-square rounded-xl border flex items-center justify-center font-bold text-lg ${
                      theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-400' : 'bg-white/5 border border-white/10 text-slate-600'
                    }`}>+</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs font-bold">
                    <div className="space-y-1">
                      <span className={`text-[10px] ${theme === 'light' ? 'text-zinc-450' : 'text-slate-550'}`}>Grid Gap: 8px</span>
                      <div className={`h-1 rounded-full ${theme === 'light' ? 'bg-zinc-100' : 'bg-white/10'}`}><div className="h-full bg-[#aace16] w-[40%]" /></div>
                    </div>
                    <div className="space-y-1">
                      <span className={`text-[10px] ${theme === 'light' ? 'text-zinc-450' : 'text-slate-550'}`}>Corner Radius: 12px</span>
                      <div className={`h-1 rounded-full ${theme === 'light' ? 'bg-zinc-100' : 'bg-white/10'}`}><div className="h-full bg-[#aace16] w-[60%]" /></div>
                    </div>
                  </div>
                </div>
              )}

              {mockupType === 'ai' && (
                <div className="space-y-6 py-4">
                  <div className="space-y-2">
                    <label className={`text-[10px] font-bold uppercase tracking-wider ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>AI Prompt Input</label>
                    <div className={`px-4 py-3 border rounded-xl text-xs font-semibold min-h-[60px] ${
                      theme === 'light' ? 'bg-zinc-50 border-zinc-250 text-zinc-800' : 'bg-black/40 border border-white/10 text-slate-300'
                    }`}>
                      A hyperrealistic dynamic image of a cybernetic tiger walking in neon cyberpunk alleyway...
                    </div>
                  </div>
                  <div className={`flex items-center justify-between text-[10px] font-bold p-2 rounded-lg ${
                    theme === 'light' ? 'bg-zinc-100 text-zinc-500' : 'bg-white/5 text-slate-400'
                  }`}>
                    <span>Model: DreamSeed v4.2</span>
                    <span className="text-[#aace16]">Seed: 98127391</span>
                  </div>
                </div>
              )}

              {mockupType === 'stock' && (
                <div className="space-y-6 py-4">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
                    <input 
                      disabled
                      placeholder="Search high-res stock photos..." 
                      className={`w-full pl-11 pr-4 py-3 border rounded-xl text-xs font-semibold ${
                        theme === 'light' ? 'bg-zinc-50 border-zinc-250 text-zinc-650 placeholder:text-zinc-400' : 'bg-black/40 border border-white/10 text-slate-450'
                      }`}
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2 font-bold text-[9px] text-center text-slate-400">
                    <span className={`py-1 rounded ${theme === 'light' ? 'bg-zinc-100 text-zinc-700' : 'bg-white/5'}`}>Abstract</span>
                    <span className={`py-1 rounded ${theme === 'light' ? 'bg-zinc-100 text-zinc-700' : 'bg-white/5'}`}>Minimalist</span>
                    <span className={`py-1 rounded ${theme === 'light' ? 'bg-zinc-100 text-zinc-700' : 'bg-white/5'}`}>Architecture</span>
                    <span className={`py-1 rounded ${theme === 'light' ? 'bg-zinc-100 text-zinc-700' : 'bg-white/5'}`}>Nature</span>
                  </div>
                </div>
              )}

              {mockupType === 'passport' && (
                <div className="space-y-6 py-4">
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <div className={`aspect-[3.5/4.5] rounded-2xl border p-3 flex flex-col items-center justify-center relative overflow-hidden ${
                      theme === 'light' ? 'bg-zinc-50 border-zinc-250' : 'bg-white/5 border border-white/10'
                    }`}>
                      <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#aace16] flex items-center justify-center text-[#aace16] mb-2">
                        <User className="w-8 h-8" />
                      </div>
                      <span className={`text-[10px] font-mono font-bold ${theme === 'light' ? 'text-zinc-800' : 'text-slate-300'}`}>Biometric Guide</span>
                      <span className={`text-[8px] ${theme === 'light' ? 'text-zinc-400' : 'text-slate-500'}`}>ISO/IEC 19794-5</span>
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-[#aace16]/20 text-[#aace16] text-[8px] font-bold">2x2"</div>
                    </div>
                    <div className="space-y-3 text-left">
                      <div className={`p-3 rounded-xl border space-y-1 ${theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/5 border border-white/5'}`}>
                        <span className={`text-[10px] font-bold block ${theme === 'light' ? 'text-zinc-500' : 'text-slate-300'}`}>Target Standard</span>
                        <span className="text-xs font-mono font-bold text-[#aace16]">US Passport (2x2 in)</span>
                      </div>
                      <div className={`p-3 rounded-xl border space-y-1 ${theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/5 border border-white/5'}`}>
                        <span className={`text-[10px] font-bold block ${theme === 'light' ? 'text-zinc-500' : 'text-slate-300'}`}>Print Grid Layout</span>
                        <span className={`text-xs font-mono font-bold ${theme === 'light' ? 'text-zinc-700' : 'text-slate-300'}`}>6 Photos / 4x6 Sheet</span>
                      </div>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between font-mono text-[9px] font-bold p-2.5 rounded-lg ${
                    theme === 'light' ? 'bg-zinc-100 text-zinc-500' : 'bg-black/20 text-slate-550'
                  }`}>
                    <span>Auto Background Scrub</span>
                    <span className="text-[#aace16]">Biometric Face Crop ✓</span>
                  </div>
                </div>
              )}

              {/* Action Floating overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="px-5 py-2.5 rounded-xl bg-white text-black text-xs font-extrabold uppercase tracking-widest transition-transform duration-300 transform translate-y-4 group-hover:translate-y-0">
                  Launch Interactive Studio
                </span>
              </div>

            </div>
          </div>
        </div>

        {/* Feature Comparison Section */}
        <div className={`pt-16 border-t ${theme === 'light' ? 'border-zinc-200' : 'border-white/5'}`}>
          <div className="text-center max-w-xl mx-auto mb-12 space-y-2">
            <h2 className={`text-xl md:text-2xl font-black uppercase tracking-tight ${theme === 'light' ? 'text-zinc-950' : 'text-white'}`}>
              Smart Imagify vs Cloud Compressors
            </h2>
            <p className={`text-xs font-semibold leading-relaxed ${theme === 'light' ? 'text-zinc-400' : 'text-slate-500'}`}>
              Why client-side offline rendering beats traditional cloud-processing.
            </p>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto text-xs font-semibold ${theme === 'light' ? 'text-zinc-700' : 'text-slate-300'}`}>
            <div className={`p-6 rounded-2xl border space-y-4 ${
              theme === 'light' ? 'border-[#aace16]/30 bg-[#aace16]/[0.02]' : 'border border-[#aace16]/10 bg-[#aace16]/[0.01]'
            }`}>
              <span className="text-[#aace16] font-black uppercase tracking-wider block">Smart Imagify Client Engine</span>
              <ul className="space-y-2.5">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#aace16]" /> 100% Private (No files ever leave your device)</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#aace16]" /> Instantly fast (Uses GPU/Canvas hardware)</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#aace16]" /> Batch compile hundreds of MBs in seconds</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#aace16]" /> Runs fully offline without internet connections</li>
              </ul>
            </div>

            <div className={`p-6 rounded-2xl border space-y-4 ${
              theme === 'light' ? 'border-zinc-200 bg-zinc-50/50' : 'border border-white/5 bg-white/[0.005]'
            }`}>
              <span className="text-slate-500 font-black uppercase tracking-wider block">Standard Cloud Compressors</span>
              <ul className={`space-y-2.5 ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>
                <li className="flex items-center gap-2"><span className="text-rose-500">✕</span> Files uploaded to third-party databases</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">✕</span> Heavy bandwidth load & waiting timers</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">✕</span> Max file restrictions (e.g. 5MB limit)</li>
                <li className="flex items-center gap-2"><span className="text-rose-500">✕</span> Watermarks added to force upgrades</li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    );
  };

  // Coming Soon Layout Wrapper
  const ComingSoonWrapper = ({ 
    title, 
    keywords, 
    icon: IconComponent, 
    features, 
    description,
    mockUpTitle,
    mockUpDescription
  }: { 
    title: string, 
    keywords: string[], 
    icon: React.ComponentType<any>, 
    features: string[], 
    description: string,
    mockUpTitle: string,
    mockUpDescription: string
  }) => (
    <div className={`${theme === 'light' ? 'text-zinc-800' : 'text-slate-100'} px-4 sm:px-6 lg:px-8 relative font-sans w-full max-w-7xl mx-auto`}>
      <div className={`absolute top-1/4 left-1/4 -translate-y-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none -z-10 ${
        theme === 'light' ? 'bg-zinc-200/30' : 'bg-[#aace16]/5'
      }`} />
      <div className={`absolute top-1/3 right-1/4 -translate-y-1/2 translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none -z-10 ${
        theme === 'light' ? 'bg-emerald-500/2' : 'bg-emerald-500/5'
      }`} />

      <div className="space-y-10 py-6">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* LEFT: COPYWRITING & BENEFITS */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#aace16]/10 border border-[#aace16]/20 rounded-full text-xs font-black text-[#aace16] uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-[#aace16]" />
              COMING SOON · PRE-INDEXED
            </div>

            <div className="space-y-4">
              <h1 className={`text-4xl md:text-5xl tracking-tight leading-tight ${
                theme === 'light' ? 'text-zinc-950' : 'text-white'
              }`}>
                {renderFormattedTitle(title, theme)}
              </h1>
              <div className="flex flex-wrap gap-2 pt-1">
                {keywords.map(kw => (
                  <span key={kw} className={`px-2 py-0.5 border rounded-md text-[9px] font-mono ${
                    theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-500' : 'bg-white/5 border border-white/10 text-slate-400'
                  }`}>
                    #{kw}
                  </span>
                ))}
              </div>
            </div>

            {/* Rich 300+ Word Semantic Text Block */}
            <div className={`space-y-5 text-sm leading-relaxed font-sans pr-4 ${
              theme === 'light' ? 'text-zinc-650' : 'text-slate-300'
            }`}>
              <p>
                {description}
              </p>
              <p>
                Like all premium modules hosted inside the Smart Imagify workspace, this utility is developed on deep client-side offline architectures. 
                Instead of processing sensitive data files across high latency remote cloud networks, our upcoming engine takes complete advantage of your system's localized browser threads. 
                This promises absolute file privacy, 10x compression loading capabilities, and completely free operations without file size limit penalties.
              </p>
              <p>
                By avoiding traditional cloud uploads, we fully protect your assets from third-party leakage while delivering instantaneous results directly under 50KB specifications. 
                We are fine-tuning high performance multi-core processing to maintain crisp layouts with zero cumulative layout shifts.
              </p>
            </div>

            {/* Features Accordion List */}
            <div className="space-y-3 pt-2">
              <h3 className={`text-xs font-black uppercase tracking-widest font-mono ${
                theme === 'light' ? 'text-zinc-500' : 'text-white'
              }`}>CORE UTILITY HIGHLIGHTS</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((feat, i) => (
                  <div key={i} className={`flex items-start gap-2.5 p-3 rounded-xl border ${
                    theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/[0.02] border-white/5'
                  }`}>
                    <Check className="w-4 h-4 text-[#aace16] shrink-0 mt-0.5" />
                    <span className={`text-xs font-medium ${theme === 'light' ? 'text-zinc-700' : 'text-slate-300'}`}>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Captivating Waitlist Notify Form */}
            <div className={`p-6 rounded-2xl space-y-4 relative overflow-hidden border ${
              theme === 'light' ? 'bg-[#aace16]/5 border-[#aace16]/25' : 'bg-[#aace16]/5 border-[#aace16]/10'
            }`}>
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#aace16]/10 rounded-full blur-xl pointer-events-none" />
              
              <div className="space-y-1">
                <h4 className={`text-sm font-bold flex items-center gap-1.5 ${theme === 'light' ? 'text-zinc-950' : 'text-white'}`}>
                  <Mail className="w-4 h-4 text-[#aace16]" />
                  Subscribe to Beta Release Waitlist
                </h4>
                <p className={`text-xs ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Be the first to know when local browser compilation starts. Zero spam, unsubscribe anytime.</p>
              </div>

              {submitted ? (
                <div className="p-3 bg-[#aace16]/15 border border-[#aace16]/30 text-[#aace16] rounded-xl text-xs font-bold text-center animate-fade-in-up">
                  🎉 Registration successful! We will notify you at launch!
                </div>
              ) : (
                <form onSubmit={handleNotifySubmit} className="flex gap-2">
                  <input 
                    type="email" 
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email address" 
                    className={`flex-1 px-4 py-2.5 border rounded-xl text-xs focus:outline-none focus:border-[#aace16] focus:ring-1 focus:ring-[#aace16] transition-all font-semibold ${
                      theme === 'light'
                        ? 'bg-white border-zinc-250 text-zinc-900 placeholder:text-zinc-400'
                        : 'bg-black/40 border border-white/10 text-slate-200 placeholder:text-slate-500'
                    }`}
                  />
                  <button 
                    type="submit"
                    className="px-5 py-2.5 bg-[#aace16] hover:bg-[#bdef2b] text-black font-sans text-xs font-black uppercase rounded-xl transition-all shadow-md shadow-[#aace16]/10 hover:shadow-[#aace16]/25 shrink-0 cursor-pointer flex items-center gap-1.5"
                  >
                    <span>Notify Me</span>
                    <Send className="w-3 h-3" />
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* RIGHT: PREMIUM VISUAL PREVIEW & SPECS */}
          <div className="lg:col-span-5 space-y-6">
            {/* Holographic Tool Preview Simulator Card */}
            <div className={`border rounded-2xl p-5 relative overflow-hidden select-none border-t-2 border-t-[#aace16]/20 ${
              theme === 'light'
                ? 'border-zinc-200 bg-white shadow-xl shadow-zinc-200/30'
                : 'border-white/10 bg-[#0e0e11]/90 backdrop-blur shadow-2xl'
            }`}>
              <div className={`flex items-center justify-between border-b pb-3 mb-4 text-[10px] font-mono ${
                theme === 'light' ? 'border-zinc-100 text-zinc-400' : 'border-white/5 text-slate-500'
              }`}>
                <span>LOCAL_UTILITY_DOCK.sdk</span>
                <span className="text-[#aace16] font-bold">100% Client-Side</span>
              </div>

              {/* Glowing Inner Container */}
              <div className={`h-64 rounded-xl border border-dashed relative flex flex-col items-center justify-center text-center p-6 space-y-4 ${
                theme === 'light' ? 'border-zinc-200 bg-zinc-50/50' : 'border-white/10 bg-black/40'
              }`}>
                <div className={`w-14 h-14 rounded-2xl border flex items-center justify-center relative ${
                  theme === 'light' ? 'bg-zinc-100 border-zinc-250 text-zinc-450' : 'bg-white/5 border border-white/10 text-slate-500'
                }`}>
                  <IconComponent className="w-7 h-7 text-[#aace16]/70" />
                  <div className="absolute -inset-2 rounded-2xl border border-[#aace16]/30 animate-pulse pointer-events-none" />
                </div>
                <div className="space-y-1">
                  <h4 className={`text-xs font-extrabold uppercase tracking-widest ${theme === 'light' ? 'text-zinc-950' : 'text-slate-300'}`}>{mockUpTitle}</h4>
                  <p className={`text-[10px] max-w-[200px] leading-relaxed ${theme === 'light' ? 'text-zinc-500' : 'text-slate-500'}`}>{mockUpDescription}</p>
                </div>
                <div className="px-2.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[8px] font-bold uppercase tracking-wider border border-emerald-500/20">
                  Ready for Local Compilation
                </div>
              </div>

              {/* Local Specs Sheet */}
              <div className="mt-5 space-y-2.5">
                <h5 className={`text-[10px] font-mono font-bold uppercase tracking-widest ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>TECHNICAL SPECIFICATIONS</h5>
                <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
                  <div className={`p-2.5 rounded-lg border ${theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/[0.01] border-white/5'}`}>
                    <span className={`${theme === 'light' ? 'text-zinc-400' : 'text-slate-650'} block`}>Processing:</span>
                    <span className={`font-bold mt-0.5 block ${theme === 'light' ? 'text-zinc-800' : 'text-slate-300'}`}>100% Device Local</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/[0.01] border-white/5'}`}>
                    <span className={`${theme === 'light' ? 'text-zinc-400' : 'text-slate-650'} block`}>Required Bandwidth:</span>
                    <span className={`font-bold mt-0.5 block ${theme === 'light' ? 'text-zinc-800' : 'text-slate-300'}`}>0 KB (Offline)</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/[0.01] border-white/5'}`}>
                    <span className={`${theme === 'light' ? 'text-zinc-400' : 'text-slate-650'} block`}>Target Load Speed:</span>
                    <span className="font-bold text-emerald-405 mt-0.5 block text-emerald-550">Sub 50ms</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${theme === 'light' ? 'bg-zinc-50 border-zinc-200' : 'bg-white/[0.01] border-white/5'}`}>
                    <span className={`${theme === 'light' ? 'text-zinc-400' : 'text-slate-650'} block`}>Format Scope:</span>
                    <span className={`font-bold mt-0.5 block ${theme === 'light' ? 'text-zinc-800' : 'text-slate-300'}`}>WebP, PNG, AVIF</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy Compliance Card */}
            <div className={`p-4 rounded-xl border flex gap-3 text-left ${
              theme === 'light' ? 'border-zinc-200 bg-zinc-50/50' : 'border border-white/5 bg-white/[0.01]'
            }`}>
              <Lock className="w-5 h-5 text-[#aace16] shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h5 className={`text-xs font-bold ${theme === 'light' ? 'text-zinc-950' : 'text-slate-300'}`}>100% HIPAA & GDPR Compliant</h5>
                <p className={`text-[10px] leading-relaxed ${theme === 'light' ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Your files remain permanently locked on your local drive. We never inspect, upload, or index your original graphic elements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // PRIVACY POLICY CONTENT
  if (path === '/privacy-policy') {
    return (
      <PageWrapper title="Privacy Policy" subtitle="Last Updated: June 11, 2026" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className={`special-pages-policy-content space-y-8 max-w-4xl mx-auto text-left text-[15px] font-sans leading-relaxed transition-colors duration-[350ms] ${
          theme === 'light' ? 'text-zinc-700' : 'text-slate-300'
        }`}>

          <p>
            Smart Imagify ("we", "our", or "the Platform"), operated by <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>AYSHDAN INNOVATIONS</strong> and developed by <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Muhammad Adnan</strong>, is a 100% browser-based, client-side image processing suite. This Privacy Policy explains what data we collect, why we collect it, and how we protect it. We are committed to full transparency — <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>we do not use anyone's personal data for personal purposes, we do not store your images or files on our servers, and we never sell any data to third parties.</strong>
          </p>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              1. 100% Local, Browser-Based Processing
            </h3>
            <p>
              The core principle of Smart Imagify is that <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>all image processing happens entirely inside your web browser</strong> using JavaScript Canvas 2D, WebAssembly, and Web Workers. Your photos, images, and files are <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>never uploaded to any server</strong>. Nothing is transmitted over the internet. Everything runs on your own PC, laptop, or mobile device.
            </p>
            <p>This applies to every tool on our platform, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Single Image Compressor</li>
              <li>Bulk Image Compressor</li>
              <li>Image Resizer</li>
              <li>Image Format Converter</li>
              <li>Collage Maker</li>
              <li>Passport Photo Maker</li>
              <li>AI Image Generator</li>
              <li>Stock Photo Library</li>
              <li>Background Remover</li>
              <li>Image Upscaler</li>
              <li>Watermark Remover</li>
              <li>Compression History Log</li>
            </ul>
            <p>
              We do not store, inspect, cache, log, or transmit any of your photos, images, or processed files. Your original files and their optimized outputs remain exclusively on your own device at all times.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              2. Information We Collect
            </h3>
            <p>We collect only the absolute minimum data needed to provide account functionality and manage credits:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Account Details:</strong> Full name, email address, phone number, and country — collected during sign-up (email/password) or via Google OAuth.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Profile Picture:</strong> Optionally uploaded avatar, stored in Supabase Storage for display purposes only.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Credit Balance & Plan:</strong> We track your credit balance and subscription plan tier to manage access to premium tools.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Credit Transaction Ledger:</strong> A log of credit additions and deductions.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Support Tickets:</strong> Messages you send through our in-app support system.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Referral Records:</strong> Stored referral relationships.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Guest Device Fingerprint:</strong> Generated locally to track guest credit usage. This is a non-identifying technical hash.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              3. How We Use Your Information
            </h3>
            <p>We guarantee that any personal data collected is used solely and exclusively for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>To create, authenticate, and manage your Smart Imagify user account.</li>
              <li>To manage your credit balance and subscription packages.</li>
              <li>To process referral bonuses and promotional credit rewards.</li>
              <li>To respond to your support tickets and direct inquiries.</li>
              <li>To maintain your dashboard settings and preferences.</li>
            </ul>
            <p className="mt-4">
              We never sell, rent, trade, or share your personal information with third-party advertisers, data brokers, or marketing agencies. We never use your data for personal purposes or store your photos or images on our servers.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              4. Credit System & Package Management
            </h3>
            <p>
              Smart Imagify operates on a credit-based system. When you sign up, you receive free credits. You can earn more credits through referrals or by upgrading to a paid plan.
            </p>
            <p>
              The only reason we maintain your account data is to manage this credit system and provide you access to the platform tools. We do not mine, profile, or analyze your personal information for any other purpose.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              5. Local Storage & IndexedDB
            </h3>
            <p>
              Smart Imagify uses your browser's localStorage and IndexedDB to store non-sensitive preferences and compression history logs locally on your device. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Theme preference (dark/light mode)</li>
              <li>Default compression quality settings</li>
              <li>Compression history logs (file names, sizes, timestamps)</li>
              <li>Cached session tokens for seamless sign-in</li>
              <li>Promotional popup dismissal cooldowns</li>
            </ul>
            <p>This data is stored only on your own device and is never transmitted to our servers.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              6. Third-Party API Services
            </h3>
            <p>Certain features connect to third-party APIs. These are the only cases where any data leaves your browser:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>AI Image Generator:</strong> Text prompts you enter are sent to HuggingFace or Stability AI endpoints. Only your text prompt is sent.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Stock Photo Library:</strong> Search queries are sent to Pexels and Pixabay APIs.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Authentication:</strong> Credentials are processed by Supabase Auth using secure OAuth 2.0.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              7. Data Protection & Security
            </h3>
            <p>
              Your account details are securely stored using industry-standard AES-256 encryption via Supabase. All database connections use encrypted TLS channels. Row Level Security policies ensure users can only access their own data.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              8. Global Compliance (GDPR, CCPA & CPRA)
            </h3>
            <p>Smart Imagify is designed with privacy-first architecture, ensuring compliance with international regulations:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Right to Access & Delete:</strong> You can permanently delete your account and all associated data through your dashboard.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Right to Portability:</strong> You may request a complete export of your account data.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>No Cross-Site Tracking:</strong> We do not utilize tracking cookies or cross-site tracking pixels.</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Data Minimization:</strong> We collect only what is strictly necessary for account and credit management.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              9. Children's Privacy
            </h3>
            <p>
              Smart Imagify does not knowingly collect personal information from children under the age of 13. If we become aware that a child has provided us with personal information, we will delete that information immediately.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              10. Changes to This Policy
            </h3>
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              11. Contact Information
            </h3>
            <p>
              If you have any questions regarding our privacy practices or wish to exercise your data rights, please contact us:
            </p>
            <div className="space-y-1 text-sm">
              <p><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>AYSHDAN INNOVATIONS</strong></p>
              <p>Developer: Muhammad Adnan</p>
              <p>Email: <a href="mailto:support@smartimagify.com" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">support@smartimagify.com</a></p>
              <p>Website: <a href="https://www.ayshdan.com" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">www.ayshdan.com</a></p>
            </div>
          </section>

        </div>
      </PageWrapper>
    );
  }

  // TERMS OF SERVICE CONTENT
  if (path === '/terms-of-service') {
    return (
      <PageWrapper title="Terms of Service" subtitle="Effective Date: June 11, 2026" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className={`special-pages-policy-content space-y-8 max-w-4xl mx-auto text-left text-[15px] font-sans leading-relaxed transition-colors duration-[350ms] ${
          theme === 'light' ? 'text-zinc-700' : 'text-slate-300'
        }`}>

          <p>
            By accessing, browsing, or using Smart Imagify ("the Platform"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms"). Smart Imagify is developed and operated by <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Muhammad Adnan</strong> under <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>AYSHDAN INNOVATIONS</strong>. If you do not agree with these Terms, please discontinue use of the Platform immediately.
          </p>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              1. License & Proprietary Rights
            </h3>
            <p>Smart Imagify grants you a <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>non-exclusive, non-transferable, fully revocable license</strong> to use the Platform for personal, commercial, educational, and developer-centric image processing purposes. All intellectual property, including but not limited to source code, UI designs, algorithms, brand logos, and documentation, remains the exclusive property of AYSHDAN INNOVATIONS.</p>
            <p>You agree not to copy, distribute, reverse engineer, decompile, disassemble, or create derivative works from any part of the Platform without prior written consent from AYSHDAN INNOVATIONS.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              2. Account Registration & Credit System
            </h3>
            <p>To access premium features, you may create an account using email/password or Google OAuth. You are responsible for maintaining the confidentiality of your account credentials. Upon registration, you receive free credits. Additional credits can be earned through referrals and promotional activities, or purchased by upgrading to a paid subscription plan.</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Credits are non-refundable and non-transferable between accounts.</li>
              <li>Credits are consumed when using premium tools (Image Compressor, AI Image Generator, Bulk Compressor, etc.).</li>
              <li>We reserve the right to adjust credit values, pricing tiers, and bonus structures at any time.</li>
              <li>Guest users receive limited trial credits tied to device fingerprints.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              3. User Obligations & Acceptable Use
            </h3>
            <p>Because all image processing runs entirely client-side in your browser, you are <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>fully and exclusively responsible</strong> for the content, licensing, copyright ownership, and distribution of all graphic assets processed through the Platform. You agree to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Not use the Platform to process illegal, harmful, or infringing content.</li>
              <li>Not attempt to exploit, hack, or circumvent the credit system or subscription checks.</li>
              <li>Not use automated bots or scripts to interact with the Platform.</li>
              <li>Not impersonate other users or access accounts that do not belong to you.</li>
              <li>Comply with all applicable local, state, national, and international laws.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              4. Intellectual Property & Output Ownership
            </h3>
            <p>All images you upload, process, compress, resize, or convert through Smart Imagify remain <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>your exclusive property</strong>. We claim no ownership or license over your files. Since all processing is local, your files never enter our possession.</p>
            <p>Images generated through the AI Image Generator are produced by third-party AI models. You may use generated images freely, but we make no guarantee of exclusivity. You are responsible for ensuring AI-generated outputs do not infringe on existing copyrights or trademarks.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              5. Paid Subscriptions & Refund Policy
            </h3>
            <p>Smart Imagify offers paid subscription tiers that provide additional credits and enhanced capabilities. By purchasing a subscription:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You agree to pay the fees displayed at the time of purchase.</li>
              <li>Subscriptions may auto-renew unless cancelled before the renewal date.</li>
              <li>Refunds are handled on a case-by-case basis. Contact <a href="mailto:support@smartimagify.com" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">support@smartimagify.com</a> for refund requests.</li>
              <li>Credits from paid plans are credited to your account immediately upon activation.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              6. Service Availability & Limitations
            </h3>
            <p>Our offline-centric toolkit runs directly on your device CPU and browser engine. We are not responsible for performance constraints, memory limitations, or file-saving issues caused by older browser versions, low hardware configurations, or third-party browser extensions. We aim to maintain 99.9% uptime for our authentication and account management services, but do not guarantee uninterrupted access.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              7. Termination & Suspension
            </h3>
            <p>We reserve the right to suspend, block, or permanently delete any user account that violates these Terms, including but not limited to: attempting to bypass credit systems, engaging in abusive behavior, reverse engineering the Platform, or distributing the Platform's source code. Upon termination, all associated credits and data may be permanently deleted.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              8. Governing Law & Changes
            </h3>
            <p>These Terms shall be governed by and construed in accordance with applicable international digital commerce laws. AYSHDAN INNOVATIONS reserves the right to modify these Terms at any time. Continued use of the Platform after changes constitutes your acceptance of the updated Terms.</p>
            <div className="space-y-1 text-sm mt-4">
              <p><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>AYSHDAN INNOVATIONS</strong></p>
              <p>Email: <a href="mailto:support@smartimagify.com" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">support@smartimagify.com</a></p>
              <p>Website: <a href="https://www.ayshdan.com" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">www.ayshdan.com</a></p>
            </div>
          </section>

        </div>
      </PageWrapper>
    );
  }

  if (path === '/disclaimer') {
    return (
      <PageWrapper title="Disclaimer of Liability" subtitle="Published: June 11, 2026" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className={`special-pages-policy-content space-y-8 max-w-4xl mx-auto text-left text-[15px] font-sans leading-relaxed transition-colors duration-[350ms] ${
          theme === 'light' ? 'text-zinc-700' : 'text-slate-300'
        }`}>

          <p>
            Please review this document carefully to understand your responsibilities and the limitations of liability when using Smart Imagify for offline file conversions, image processing, and AI-generated outputs.
          </p>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              1. "As-Is" Platform Warranty
            </h3>
            <p>Smart Imagify is provided on an <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>"as-is"</strong> and <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>"as-available"</strong> basis. Muhammad Adnan and AYSHDAN INNOVATIONS make no representations or warranties of any kind, express or implied, regarding the completeness, accuracy, reliability, suitability, or availability of the Platform. We do not guarantee that our client-side tools will execute error-free on every operating system, browser, or device configuration.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              2. Loss of Data Liability
            </h3>
            <p>Because all compression, resizing, format conversion, and image editing operations run entirely within your browser's RAM sandbox, any sudden browser crash, tab closure, device shutdown, or battery failure could cause immediate loss of your current processing queue. <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>We strongly advise maintaining original backup copies of all files before processing.</strong> AYSHDAN INNOVATIONS accepts zero liability for any data loss, file corruption, or memory crashes.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              3. AI Image Generator Disclaimer
            </h3>
            <p>Images generated through the AI Image Generator are produced by third-party machine learning models (HuggingFace / Stability AI). AYSHDAN INNOVATIONS accepts zero liability for any matching trademark coincidences, graphic visual similarities, copyright claims, or moral licensing constraints arising from prompts entered by users. Users are solely responsible for verifying the legality of generated content before commercial use.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              4. Third-Party Services & External Links
            </h3>
            <p>Smart Imagify integrates with third-party APIs (Pexels, Pixabay, Supabase, HuggingFace) to provide stock photo search, AI generation, and authentication services. We do not control these external services and are not responsible for their availability, accuracy, or privacy practices. Use of these third-party services is subject to their own terms and conditions.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              5. Limitation of Liability
            </h3>
            <p>To the maximum extent permitted by law, AYSHDAN INNOVATIONS, its developer Muhammad Adnan, and all associated parties shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform, including but not limited to loss of data, loss of profits, or business interruption. Our total liability shall not exceed the amount you paid for the service in the 12 months preceding the claim.</p>
            <div className="space-y-1 text-sm mt-4">
              <p>For questions or concerns about this Disclaimer, contact us at <a href="mailto:support@smartimagify.com" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">support@smartimagify.com</a></p>
            </div>
          </section>

        </div>
      </PageWrapper>
    );
  }

  // COOKIE POLICY CONTENT
  if (path === '/cookie-policy') {
    return (
      <PageWrapper title="Cookie Policy" subtitle="Last Updated: June 11, 2026" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className={`special-pages-policy-content space-y-8 max-w-4xl mx-auto text-left text-[15px] font-sans leading-relaxed transition-colors duration-[350ms] ${
          theme === 'light' ? 'text-zinc-700' : 'text-slate-300'
        }`}>

          <p>
            Smart Imagify uses <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>minimal browser storage mechanisms</strong> (localStorage and IndexedDB) instead of traditional tracking cookies. We do not use third-party tracking cookies, advertising cookies, or cross-site analytics cookies. This policy explains exactly what data is stored in your browser and why.
          </p>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              1. What We Store Locally
            </h3>
            <p>Smart Imagify uses your browser's <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>localStorage</strong> to store essential functionality preferences. These are not traditional HTTP cookies — they are key-value pairs stored locally on your device and never transmitted to any server automatically.</p>
            <div className="overflow-x-auto mt-4">
              <table className={`w-full text-sm text-left border rounded-xl overflow-hidden ${
                theme === 'light' ? 'border-slate-200' : 'border-white/10'
              }`}>
                <thead>
                  <tr className={theme === 'light' ? 'bg-slate-100 text-zinc-800 text-xs font-bold uppercase tracking-wider' : 'bg-white/5 text-slate-300 text-xs font-bold uppercase tracking-wider'}>
                    <th className={`px-4 py-3 border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>Storage Key</th>
                    <th className={`px-4 py-3 border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>Purpose</th>
                    <th className={`px-4 py-3 border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>Duration</th>
                  </tr>
                </thead>
                <tbody className={theme === 'light' ? 'text-zinc-600' : 'text-slate-400'}>
                  <tr className={`border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-emerald-600 dark:text-emerald-400">si_user_profile</td>
                    <td className="px-4 py-2.5">Cached user session data (name, email, credits)</td>
                    <td className="px-4 py-2.5">Until sign-out</td>
                  </tr>
                  <tr className={`border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-emerald-600 dark:text-emerald-400">i_settings</td>
                    <td className="px-4 py-2.5">Theme preference (dark/light mode)</td>
                    <td className="px-4 py-2.5">Persistent</td>
                  </tr>
                  <tr className={`border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-emerald-600 dark:text-emerald-400">si_topbar_dismissed_time</td>
                    <td className="px-4 py-2.5">Prevents banner re-display for 24 hours</td>
                    <td className="px-4 py-2.5">24 hours</td>
                  </tr>
                  <tr className={`border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/5'}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-emerald-600 dark:text-emerald-400">si_offer_dismissed_time_*</td>
                    <td className="px-4 py-2.5">Prevents promotional popup re-display</td>
                    <td className="px-4 py-2.5">24 hours</td>
                  </tr>
                  <tr className={theme === 'light' ? '' : ''}>
                    <td className="px-4 py-2.5 font-mono text-xs text-emerald-600 dark:text-emerald-400">si_active_plan</td>
                    <td className="px-4 py-2.5">Current subscription tier</td>
                    <td className="px-4 py-2.5">Until sign-out</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              2. IndexedDB (Compression History)
            </h3>
            <p>Your compression history log (file names, original sizes, compressed sizes, timestamps) is stored in your browser's <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>IndexedDB</strong> database. This data is stored <strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>exclusively on your device</strong> and is never synced to our servers. You can clear this data at any time from the History tab in the dashboard or by clearing your browser data.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              3. Authentication Cookies (Supabase)
            </h3>
            <p>When you sign in, Supabase Auth sets secure, httpOnly session cookies or localStorage tokens to maintain your authenticated session. These are essential for keeping you signed in and are automatically removed when you sign out. They do not track your browsing activity on other websites.</p>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              4. What We Do NOT Use
            </h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>No third-party tracking cookies</strong> (Google Analytics, Facebook Pixel, etc.)</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>No advertising cookies</strong> or retargeting scripts</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>No cross-site tracking</strong> or browser fingerprinting for marketing</li>
              <li><strong className={`font-semibold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>No data sharing</strong> with ad networks or analytics platforms</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              5. Managing & Clearing Stored Data
            </h3>
            <p>You can clear all Smart Imagify stored data at any time by:</p>
            <ul className="list-disc pl-6 space-y-1.5 font-medium">
              <li>Signing out from your account (clears session data)</li>
              <li>Clearing your browser's localStorage and IndexedDB through browser settings</li>
              <li>Using your browser's Developer Tools → Application → Storage → Clear</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className={`text-xl font-bold transition-colors ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
              6. Contact Information
            </h3>
            <div className="space-y-1 text-sm">
              <p>For questions about this Cookie Policy, contact us at <a href="mailto:support@smartimagify.com" className="text-emerald-500 hover:text-emerald-400 dark:text-emerald-400 dark:hover:text-emerald-300 underline font-medium">support@smartimagify.com</a></p>
            </div>
          </section>

        </div>
      </PageWrapper>
    );
  }

  // ACTIVE TOOLS MARKETING LANDING PAGES

  if (path === '/compress-image') {
    return (
      <ActiveToolLandingWrapper
        title="Lossless Image Compressor"
        subtitle="Compress Image Online"
        description="Shrink your images by up to 90% without losing quality. Our smart client-side engine parses your images using local hardware threads. No upload limits, no data leaks, and absolute safety."
        features={[
          'Instant offline local processing',
          'Lossless pixel matching guarantee',
          'Supports JPG, PNG, WebP, & AVIF',
          'No upload bandwidth delays'
        ]}
        icon={ImageIcon}
        toolPath="/compress-image"
        mockupType="compress"
      />
    );
  }

  if (path === '/bulk-image-compressor') {
    return (
      <ActiveToolLandingWrapper
        title="Bulk ZIP Image Compressor"
        subtitle="Bulk Image Compressor"
        description="Select hundreds of images at once and compress them into a single high-quality archive. Leverages multi-core parallel web workers to compress your folders in a blink."
        features={[
          'Parallel multi-core processing',
          'Zip download in one click',
          'Handles up to 200 images at once',
          'Zero cloud server queue times'
        ]}
        icon={Layers}
        toolPath="/bulk-image-compressor"
        mockupType="bulk"
      />
    );
  }

  if (path === '/image-resizer') {
    return (
      <ActiveToolLandingWrapper
        title="Interactive Image Resizer"
        subtitle="Image Resizer Online"
        description="Scale, crop, and adjust your photos to exact pixel width or percentage dimensions. Uses high-fidelity lanczos resampling for crisp borders and clear results."
        features={[
          'Lanczos-3 resampling filter',
          'Lock aspect ratio toggle',
          'Custom dimension scaling',
          'Realtime canvas preview updates'
        ]}
        icon={Maximize}
        toolPath="/image-resizer"
        mockupType="resize"
      />
    );
  }

  if (path === '/image-format-converter') {
    return (
      <ActiveToolLandingWrapper
        title="Image Format Converter"
        subtitle="Image Format Converter Online"
        description="Convert your graphic assets between next-gen formats like WebP, AVIF, PNG, JPG, and SVG. Protect alpha transparency channels and optimize export color space ranges."
        features={[
          'WebP and AVIF next-gen export',
          'Retain alpha transparency layers',
          'Offline pixel buffer matching',
          'Batch output format mapping'
        ]}
        icon={RefreshCw}
        toolPath="/image-format-converter"
        mockupType="convert"
      />
    );
  }

  if (path === '/collage-maker') {
    return (
      <ActiveToolLandingWrapper
        title="Creative Grid Collage Maker"
        subtitle="Collage Maker Online"
        description="Stitch multiple photos into beautiful, customizable grid layouts. Fine-tune corner rounding, gap width spacing, and canvas background properties in real-time."
        features={[
          '150+ organic geometric layouts',
          'Dynamic spacing & corner sliders',
          'High resolution export prints',
          'Hardware-accelerated drag-drop'
        ]}
        icon={LayoutGrid}
        toolPath="/collage-maker"
        mockupType="collage"
      />
    );
  }

  if (path === '/ai-image-generator') {
    return (
      <ActiveToolLandingWrapper
        title="DreamSeed AI Image Studio"
        subtitle="AI Image Generator"
        description="Transform descriptive text prompts into breathtaking digital art. Runs highly optimized local generative prompts directly within the sandboxed creative viewport."
        features={[
          'Advanced DreamSeed model weights',
          'Custom seed and resolution controls',
          'No limit on prompt combinations',
          'Fast pixel rendering pipeline'
        ]}
        icon={Sparkles}
        toolPath="/ai-image-generator"
        mockupType="ai"
      />
    );
  }

  if (path === '/stock-library') {
    return (
      <ActiveToolLandingWrapper
        title="High-Res Royalty Free Stock"
        subtitle="Stock Library Search"
        description="Browse, filter, and import millions of stunning high-resolution stock photographs directly into your active design canvas. Instant royalty-free commercial license."
        features={[
          'Millions of free commercial assets',
          'Advanced query search filtering',
          'One-tap canvas layout loading',
          'Curated daily abstract updates'
        ]}
        icon={Search}
        toolPath="/stock-library"
        mockupType="stock"
      />
    );
  }

  if (path === '/passport-photo-maker') {
    return (
      <ActiveToolLandingWrapper
        title="Biometric Passport Photo Maker"
        subtitle="Passport Photo Maker Online"
        description="Create official biometric ID, visa, and passport photos online for free. Automatically align facial landmarks, remove backgrounds, and export standardized print sheets (2x2 inch, 35x45mm) locally in your browser."
        features={[
          'ISO-compliant biometric guides',
          'Standard US, UK & EU visa sizes',
          'Automatic 4x6 print grid builder',
          '100% private device-local processing'
        ]}
        icon={IdCard}
        toolPath="/passport-photo-maker"
        mockupType="passport"
      />
    );
  }

  // DEDICATED PRICING PAGE
  if (path === '/pricing') {
    const localFallbackPackages = [
      {
        id: "basic-fallback",
        slug: "basic",
        name: "BASIC",
        price_monthly: 1.00,
        price_yearly: 10.00,
        stripe_monthly_link: "https://stripe.com/mock/basic-monthly",
        stripe_yearly_link: "https://stripe.com/mock/basic-yearly",
        is_active: true,
        sort_order: 1,
        credits: 50
      },
      {
        id: "plus-fallback",
        slug: "plus",
        name: "PLUS",
        price_monthly: 5.00,
        price_yearly: 39.00,
        stripe_monthly_link: "https://stripe.com/mock/plus-monthly",
        stripe_yearly_link: "https://stripe.com/mock/plus-yearly",
        is_active: true,
        sort_order: 2,
        credits: 300
      },
      {
        id: "ultra-fallback",
        slug: "ultra",
        name: "ULTRA",
        price_monthly: 12.00,
        price_yearly: 99.00,
        stripe_monthly_link: "https://stripe.com/mock/ultra-monthly",
        stripe_yearly_link: "https://stripe.com/mock/ultra-yearly",
        is_active: true,
        sort_order: 3,
        credits: 1000
      },
      {
        id: "business-fallback",
        slug: "business",
        name: "BUSINESS",
        price_monthly: 29.00,
        price_yearly: 240.00,
        stripe_monthly_link: "https://stripe.com/mock/business-monthly",
        stripe_yearly_link: "https://stripe.com/mock/business-yearly",
        is_active: true,
        sort_order: 4,
        credits: 3000
      }
    ];

    const currentPackages = packages.length > 0 ? packages : localFallbackPackages;

    const renderedPackages = [
      {
        id: 'free-plan',
        slug: 'free',
        name: 'FREE',
        price_monthly: 0,
        price_yearly: 0,
        stripe_monthly_link: '',
        stripe_yearly_link: '',
        is_active: true,
        sort_order: 0,
        credits: 50
      },
      ...currentPackages.filter((p: any) => p.slug !== 'plus')
    ];

    const getSubtitle = (slug: string) => {
      switch (slug) {
        case 'free':
          return 'Casually test or utilize our offline conversion engines.';
        case 'starter':
        case 'basic':
          return 'For indie developers, bloggers, and simple content creators.';
        case 'pro':
        case 'plus':
        case 'ultra':
          return 'For professional designers and power developers.';
        case 'pro-team':
        case 'business':
          return 'For agencies, teams, and high-volume commercial production.';
        default:
          return 'Premium utility tier.';
      }
    };

    const getUnderButtonDesc = (slug: string) => {
      switch (slug) {
        case 'free':
          return 'Free forever. Basic limits apply.';
        default:
          return 'Lifetime access. Instant upgrade sandbox.';
      }
    };

    const getButtonText = (slug: string) => {
      switch (slug) {
        case 'free':
          return 'Get Started';
        case 'starter':
        case 'basic':
          return 'Activate Basic';
        case 'pro':
        case 'plus':
        case 'ultra':
          return 'Activate Pro';
        case 'pro-team':
        case 'business':
          return 'Activate Business';
        default:
          return 'Select Plan';
      }
    };

    const getKeyFeaturesForPlan = (slug: string) => {
      const isFree = slug === 'free';
      const isBasic = slug === 'basic' || slug === 'starter';
      const isPro = slug === 'pro' || slug === 'plus' || slug === 'ultra';
      const isBusiness = slug === 'business';
      
      return [
        { label: 'Monthly Credits', value: isFree ? '50 credits' : isBasic ? '250 credits' : isPro ? '1,000 credits' : '3,000 credits', included: true },
        { label: 'Max File Size', value: isFree ? '5 MB' : isBasic ? '10 MB' : isPro ? '50 MB' : '100 MB', included: true },
        { label: 'Parallel Workers', value: isFree ? '1 thread' : isBasic ? '2 threads' : isPro ? '4 threads' : '8 threads', included: true },
        { label: 'AI BG Removal', value: isFree ? '✕' : isBasic ? '10/month' : 'Unlimited local', included: !isFree },
        { label: 'AI Generation', value: isFree ? '✕' : isBasic ? 'Basic' : 'Advanced HD', included: !isFree },
        { label: 'Bulk Zip Engine', value: isFree ? 'Max 10 files' : isBasic ? 'Up to 100' : isPro ? 'Up to 500' : 'Unlimited', included: !isFree },
        { label: 'Cloud History', value: isFree ? '✕' : isBasic ? '✕' : 'Unlimited Sync', included: isPro || isBusiness },
        { label: 'Commercial Use', value: isFree ? '✕' : 'Single Seat', included: !isFree },
        { label: 'Support Priority', value: isFree ? 'Community' : isBasic ? 'Email (24h)' : isPro ? 'Priority (12h)' : 'VIP 24/7', included: true }
      ];
    };

    const fullComparisonFeatures = [
      { name: 'Monthly Credits Allowance', free: '50 credits', basic: '250 credits', pro: '1,000 credits', business: '3,000 credits' },
      { name: 'Maximum File Upload Size', free: '5 MB', basic: '10 MB', pro: '50 MB', business: '100 MB' },
      { name: 'Supported File Formats', free: 'JPG, PNG, WebP', basic: 'Add AVIF support', pro: 'All Formats (incl. SVG)', business: 'All Formats (incl. SVG)' },
      { name: 'AI Image Generation', free: '✕', basic: '✓ (Basic)', pro: '✓ (HD 1024px)', business: '✓ (Ultra HD 2048px)' },
      { name: 'Bulk Zip Processing', free: 'Max 10 files', basic: 'Up to 100 files', pro: 'Up to 500 files', business: 'Unlimited' },
      { name: 'Batch Downloader Engine', free: '✕', basic: '✓', pro: '✓', business: '✓' },
      { name: 'Watermark-Free Exports', free: '✓', basic: '✓', pro: '✓', business: '✓' },
      { name: 'Processing Queue Priority', free: 'Standard', basic: 'Standard', pro: 'Turbo Priority', business: 'Max Dedicated' },
      { name: 'Multi-Core Worker Threads', free: '1 thread', basic: '2 threads', pro: '4 threads', business: '8 threads' },
      { name: 'AI Background Removal', free: '✕', basic: '10/month', pro: '✓ (Unlimited local)', business: '✓ (Unlimited local)' },
      { name: 'AI Object Removal (Inpaint)', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'AI Image Expand (Outpaint)', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'AI Image Upscaler', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'AI Face & Image Enhance', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'Royalty-Free Stock Library', free: '✓', basic: '✓', pro: '✓', business: '✓' },
      { name: 'Unlimited Processing History', free: '✕', basic: '✓', pro: '✓', business: '✓' },
      { name: 'Cloud Projects Sync', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'Developer API Access', free: '✕', basic: '✕', pro: '✕', business: '✓' },
      { name: 'Workspace Access Limit', free: '1 device', basic: '1 device', pro: '3 devices', business: 'Unlimited' },
      { name: 'Commercial Usage License', free: 'Personal only', basic: 'Single Seat', pro: '✓ (Commercial)', business: '✓ (Commercial)' },
      { name: 'Team Collaboration Seats', free: '✕', basic: '✕', pro: '✕', business: '✓ (Up to 5 users)' },
      { name: 'Shared Team Credit Pool', free: '✕', basic: '✕', pro: '✕', business: '✓' },
      { name: 'Customer Support Priority', free: 'Community', basic: 'Email (24h)', pro: 'Email (12h)', business: 'VIP 24/7 Hotline' },
      { name: 'Early Access features', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'Beta Tools Enrollment', free: '✕', basic: '✕', pro: '✓', business: '✓' },
      { name: 'Future AI Tools Included', free: '✕', basic: '✕', pro: '✓', business: '✓' }
    ];

    const faqs = [
      {
        q: "What is Smart Imagify and how does it work?",
        a: "Smart Imagify is an offline-centric graphic studio that works directly inside your browser. You can compress, crop, erase backgrounds, resize, and convert images locally. No files are uploaded to external servers, ensuring absolute privacy."
      },
      {
        q: "How does the credit system work?",
        a: "Each process (like local compression or resizing) consumes 1-2 credits depending on operations. Credits are loaded automatically on your account every month based on your selected plan tier."
      },
      {
        q: "Can I use Smart Imagify completely offline?",
        a: "Yes. Once the web application loads, all compression and editing engines run locally. You can disconnect your internet and continue processing your files."
      },
      {
        q: "What is the refund policy?",
        a: "We offer a 100% money-back guarantee. If you are unsatisfied with your subscription, contact us within 14 days for a full refund of any unused credits."
      },
      {
        q: "Are future AI tools included in my plan?",
        a: "Yes! All plans include access to future AI tools and updates automatically at no extra cost during the subscription period."
      }
    ];

    const handlePlanButtonClick = (slug: string) => {
      if (onPlanSelect) {
        onPlanSelect(slug);
      } else if (onSignIn) {
        onSignIn('/pricing', 'signup');
      }
    };

    return (
      <div className={`w-full py-16 px-4 md:px-8 relative overflow-hidden font-sans transition-colors duration-300 ${
        theme === 'light' ? 'text-zinc-900 bg-white' : 'text-slate-100 bg-[#050507]'
      }`}>
        <div className="max-w-[1400px] mx-auto space-y-12 relative z-10 flex flex-col">
          
          {/* Header section */}
          <div className="text-left space-y-3 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-2 py-0.5 bg-[#aace16]/10 text-[#aace16] border border-[#aace16]/20 text-[8px] font-black rounded-full uppercase tracking-wider">
              <CreditCard className="w-3.5 h-3.5" /> Pricing Options
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase italic leading-none text-[var(--text-1)]">
              Pricing Plans
            </h2>
            <p className="text-[var(--text-3)] text-xs font-semibold leading-relaxed">
              Scale your offline creative workspace with higher file limits, priority processing queues, and advanced AI model resources.
            </p>

            {/* Toggle Cycle */}
            <div className="flex items-center gap-3 pt-2">
              <span className={`text-[10px] font-black uppercase tracking-wider transition-colors ${billingCycle === 'monthly' ? 'text-[var(--text-1)]' : 'text-[var(--text-3)]'}`}>Monthly</span>
              <button 
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                aria-label="Toggle billing cycle between Monthly and Annual"
                className="w-10 h-6 bg-[var(--bg-3)] border border-[var(--border)] rounded-full relative p-0.5 transition-all cursor-pointer shrink-0"
              >
                <div className={`w-4.5 h-4.5 bg-[var(--text-1)] rounded-full transition-all transform ${billingCycle === 'annual' ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase tracking-wider transition-colors ${billingCycle === 'annual' ? 'text-[var(--text-1)]' : 'text-[var(--text-3)]'}`}>Annual</span>
                <span className="px-2 py-0.5 bg-[#aace16]/10 text-[#aace16] border border-[#aace16]/20 text-[8px] font-black rounded-full uppercase tracking-wider">SAVE 30%</span>
              </div>
            </div>
          </div>

          {/* Grid Packages Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full pt-2">
            {renderedPackages.map((pkg) => {
              const displayPrice = billingCycle === 'annual' ? Math.floor(pkg.price_yearly / 12) : pkg.price_monthly;
              const isRecommended = pkg.slug === 'pro' || pkg.slug === 'plus' || pkg.slug === 'ultra';
              const isFree = pkg.slug === 'free';
              const subtitle = getSubtitle(pkg.slug);
              const keyFeatures = getKeyFeaturesForPlan(pkg.slug);

              return (
                <div 
                  key={pkg.id}
                  className={`group p-5 rounded-xl border flex flex-col justify-between h-full relative transition-all duration-300 bg-[var(--sidebar-bg)] ${
                    isRecommended
                      ? 'border-[#aace16]/40 shadow-sm'
                      : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                  }`}
                >
                  {/* Subtle highlight badge for recommended plan */}
                  {isRecommended && (
                    <div className="absolute top-4 right-4 px-2 py-0.5 bg-[#aace16]/15 border border-[#aace16]/30 text-[#aace16] text-[8px] font-black uppercase tracking-wider rounded-full">
                      Recommended
                    </div>
                  )}

                  <div className="flex flex-col justify-between flex-grow">
                    <div>
                      {/* Title and Subtitle */}
                      <div className="space-y-1 text-left">
                        <div className="flex items-center gap-1.5">
                          {pkg.slug === 'free' ? (
                            <Home className="w-3.5 h-3.5 text-[var(--text-3)]" />
                          ) : pkg.slug === 'starter' || pkg.slug === 'basic' ? (
                            <Home className="w-3.5 h-3.5 text-[var(--text-3)]" />
                          ) : pkg.slug === 'pro' || pkg.slug === 'plus' || pkg.slug === 'ultra' ? (
                            <Sparkles className="w-3.5 h-3.5 text-[#aace16]" />
                          ) : pkg.slug === 'pro-team' || pkg.slug === 'business' ? (
                            <Cpu className="w-3.5 h-3.5 text-zinc-400" />
                          ) : null}
                          <h3 className="text-xs font-black uppercase tracking-wider text-[var(--text-1)]">
                            {pkg.slug === 'ultra' ? 'PRO' : pkg.name}
                          </h3>
                        </div>
                        <p className="text-[10px] font-semibold leading-normal text-[var(--text-3)] min-h-[30px]">
                          {subtitle}
                        </p>
                      </div>

                      {/* Price block */}
                      <div className="flex items-baseline gap-0.5 text-left mt-4 border-b border-[var(--border)]/40 pb-4">
                        <span className="text-sm font-bold mt-0.5 text-[var(--text-1)]">$</span>
                        <span className="text-3xl font-black tracking-tight font-mono text-[var(--text-1)]">{displayPrice}</span>
                        <div className="flex flex-col text-left justify-center pl-1.5 leading-[1.1] select-none">
                          <span className="text-[8px] font-black uppercase tracking-wider text-[var(--text-2)]">
                            {isFree ? 'free forever' : billingCycle === 'annual' ? '/ month' : '/ month'}
                          </span>
                          <span className="text-[7px] font-bold font-mono uppercase tracking-wider text-[var(--text-3)]">
                            {isFree ? 'no credit card' : billingCycle === 'annual' ? 'billed annually' : 'billed monthly'}
                          </span>
                        </div>
                      </div>

                      {/* Plan Action Button */}
                      <button 
                        onClick={() => handlePlanButtonClick(pkg.slug)}
                        className={`w-full py-2.5 text-[9px] font-black uppercase tracking-widest rounded-xl transition-all mt-4 cursor-pointer flex items-center justify-center ${
                          isRecommended
                            ? 'bg-[#aace16] hover:bg-[#bdef2b] text-black border border-[#aace16]/20'
                            : 'bg-[var(--bg-3)] hover:bg-[var(--bg-2)] border border-[var(--border)] hover:border-[var(--border-hover)] text-[var(--text-1)]'
                        }`}
                      >
                        {getButtonText(pkg.slug)}
                      </button>

                      {/* Bottom description */}
                      <p className="text-[8px] font-bold text-center mt-2 text-[var(--text-3)] font-mono uppercase tracking-wider">
                        {getUnderButtonDesc(pkg.slug)}
                      </p>
                    </div>

                    {/* Features container - perfectly aligned across cards */}
                    <div className="mt-5 pt-5 border-t border-[var(--border)]/40">
                      <ul className="space-y-2 text-[10px] font-semibold text-left">
                        {keyFeatures.map((f, idx) => (
                          <li key={idx} className="flex items-center justify-between py-1 border-b border-[var(--border)]/20 last:border-0 min-h-[26px]">
                            <span className="text-[var(--text-3)] font-medium">{f.label}</span>
                            <span className={`text-[10px] font-black ${f.included ? 'text-[var(--text-1)]' : 'text-[var(--text-3)]'}`}>
                              {f.value}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-6 pt-4 text-[var(--text-3)] font-mono text-[9px] tracking-wider uppercase select-none">
            <span>✓ Security Verified</span>
            <span>·</span>
            <span>✓ No hidden charges</span>
            <span>·</span>
            <span>✓ Instant activation</span>
          </div>

          {/* Detailed Plan Comparison Specs Table */}
          <div className="bg-[var(--sidebar-bg)] border border-[var(--border)] rounded-xl p-5 overflow-hidden">
            <h3 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider mb-4">Detailed Plan Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[10px] border-collapse min-w-[700px]">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase font-mono">
                    <th className="py-2.5 font-bold">Features & Specifications</th>
                    <th className="py-2.5 font-bold">Free</th>
                    <th className="py-2.5 font-bold">Basic</th>
                    <th className="py-2.5 font-bold text-[#aace16]">Pro / Recommended</th>
                    <th className="py-2.5 font-bold">Business</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]/40 text-[var(--text-2)] font-semibold">
                  {fullComparisonFeatures.map((row, idx) => (
                    <tr key={idx} className="hover:bg-white/[0.01] transition-all">
                      <td className="py-2.5 text-[var(--text-1)]">{row.name}</td>
                      <td className="py-2.5">{row.free}</td>
                      <td className="py-2.5">{row.basic}</td>
                      <td className="py-2.5 text-[#aace16] font-bold">{row.pro}</td>
                      <td className="py-2.5">{row.business}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Accordion Section */}
          <div className="bg-[var(--sidebar-bg)] border border-[var(--border)] rounded-xl p-5 space-y-4">
            <h3 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider">Frequently Asked Questions</h3>
            <div className="space-y-2">
              {faqs.map((faq, idx) => {
                const isOpen = activePricingFaq === idx;
                return (
                  <div key={idx} className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--bg-2)]/40">
                    <button
                      onClick={() => setActivePricingFaq(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between p-3 text-[11px] font-bold text-[var(--text-1)] hover:bg-[var(--bg-3)]/50 transition-all text-left"
                    >
                      <span>{faq.q}</span>
                      <ChevronRight className={`w-3.5 h-3.5 text-[var(--text-3)] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="p-3 pt-0 text-[10px] text-[var(--text-3)] leading-relaxed border-t border-[var(--border)]/30 animate-in fade-in slide-in-from-top-1 duration-200">
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Refund Policy & Payment Methods Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[var(--sidebar-bg)] border border-[var(--border)] rounded-xl p-5 space-y-4">
              <div className="space-y-1.5">
                <h4 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider">Refund Policy</h4>
                <p className="text-[10px] text-[var(--text-3)] leading-relaxed">
                  We stand by the quality of our service with a 14-day refund policy. If you haven't consumed your plan's credits, contact support@smartimagify.com for a full, hassle-free refund.
                </p>
              </div>
              <div className="border-t border-[var(--border)]/40 pt-4 space-y-2">
                <h4 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider">Accepted Payment Methods</h4>
                <p className="text-[10px] text-[var(--text-3)] leading-relaxed">
                  We support Visa, MasterCard, American Express, Apple Pay, Google Pay, and Link. All transactions are securely processed and encrypted via Stripe.
                </p>
              </div>
            </div>

            <div className="bg-[var(--sidebar-bg)] border border-[var(--border)] rounded-xl p-5 space-y-4">
              <div className="space-y-1.5">
                <h4 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider">Lifetime Updates</h4>
                <p className="text-[10px] text-[var(--text-3)] leading-relaxed">
                  Smart Imagify is actively updated with new performance tweaks and tool additions. All future updates, optimization engines, and standard bug fixes are fully included.
                </p>
              </div>
              <div className="border-t border-[var(--border)]/40 pt-4 space-y-2">
                <h4 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider">Commercial Licensing</h4>
                <p className="text-[10px] text-[var(--text-3)] leading-relaxed">
                  Any asset you compress, resize, edit, or generate is 100% your property. All paid plans include standard commercial licensing for marketing, client work, and publishing.
                </p>
              </div>
            </div>
          </div>

          {/* Help choosing card */}
          <div className="bg-[var(--sidebar-bg)] border border-[var(--border)] rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1 text-left">
              <h4 className="text-xs font-black uppercase text-[var(--text-1)] tracking-wider">Need Help Choosing? Or Custom Seats?</h4>
              <p className="text-[10px] text-[var(--text-3)] leading-relaxed">
                Have questions about billing, workspace integration, or need custom API credit quotas? Contact sales@smartimagify.com or open a Support ticket.
              </p>
            </div>
            <a
              href="mailto:sales@smartimagify.com"
              className="px-4 py-2 bg-[var(--bg-3)] hover:bg-[var(--bg-2)] border border-[var(--border)] hover:border-[var(--border-hover)] text-[var(--text-1)] text-[10px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              Contact Sales
            </a>
          </div>

        </div>
      </div>
    );
  }

  if (path === '/faq') {

    const filteredFaqs = faqList.filter(item => 
      item.question.toLowerCase().includes(faqSearchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(faqSearchQuery.toLowerCase())
    );

    const categories = [
      { name: 'General & Support', icon: HelpCircle, id: 'general-support' },
      { name: 'Compression & Engine', icon: Cpu, id: 'compression-engine' },
      { name: 'Privacy & Security', icon: Shield, id: 'privacy-security' },
      { name: 'Plans & Billing', icon: CreditCard, id: 'plans-billing' }
    ];

    const groupedFaqs = categories.map(cat => {
      const faqs = filteredFaqs.filter(f => f.category === cat.name);
      return {
        ...cat,
        faqs
      };
    });

    const handleCategoryClick = (id: string) => {
      setActiveCategory(id);
      
      // On mobile viewports, scroll down smoothly to the questions list so the user sees the filtered FAQ cards immediately.
      if (window.innerWidth < 1024) {
        const questionsContainer = document.getElementById('faq-questions-container');
        if (questionsContainer) {
          const headerOffset = 130;
          const elementPosition = questionsContainer.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    const faqsToDisplay = faqSearchQuery 
      ? filteredFaqs 
      : activeCategory === 'all'
        ? faqList
        : faqList.filter(f => {
            const cat = categories.find(c => c.id === activeCategory);
            return cat ? f.category === cat.name : true;
          });

    return (
      <PageWrapper title="Frequently Asked Questions" subtitle="Helpful information & details" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className="w-full">

          {/* HORIZONTAL CATEGORY TABS */}
          <div className={`flex flex-wrap items-center gap-[7px] py-[15px] border-b transition-colors duration-[350ms] ${
            theme === 'light' ? 'border-[#0f0f0f]/10' : 'border-[#ffffff]/10'
          }`}>
            {/* "All" tab */}
            <button
              onClick={() => { setActiveCategory('all'); setFaqSearchQuery(''); }}
              className={`px-[15px] py-[9px] rounded-[50px] text-[11px] font-bold tracking-wide transition-all duration-[250ms] cursor-pointer select-none border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00ff85] ${
                activeCategory === 'all'
                  ? theme === 'light'
                    ? 'bg-[#0f0f0f] text-white-force border-[#0f0f0f]'
                    : 'bg-[#ffffff] text-[#000000] border-[#ffffff]'
                  : theme === 'light'
                    ? 'bg-transparent text-[#999999] border-[#0f0f0f]/10 hover:text-[#0f0f0f] hover:border-[#0f0f0f]/30'
                    : 'bg-transparent text-[#999999] border-[#ffffff]/10 hover:text-[#ffffff] hover:border-[#ffffff]/30'
              }`}
            >
              All<span className="ml-[5px] opacity-60">{faqList.length}</span>
            </button>
            {categories.map((cat) => {
              const isActive = activeCategory === cat.id;
              const count = faqList.filter(f => f.category === cat.name).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  className={`px-[15px] py-[9px] rounded-[50px] text-[11px] font-bold tracking-wide transition-all duration-[250ms] cursor-pointer select-none border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00ff85] ${
                    isActive
                      ? theme === 'light'
                        ? 'bg-[#0f0f0f] text-white-force border-[#0f0f0f]'
                        : 'bg-[#ffffff] text-[#000000] border-[#ffffff]'
                      : theme === 'light'
                        ? 'bg-transparent text-[#999999] border-[#0f0f0f]/10 hover:text-[#0f0f0f] hover:border-[#0f0f0f]/30'
                        : 'bg-transparent text-[#999999] border-[#ffffff]/10 hover:text-[#ffffff] hover:border-[#ffffff]/30'
                  }`}
                >
                  {cat.name}<span className="ml-[5px] opacity-60">{count}</span>
                </button>
              );
            })}

            {/* Search */}
            <div className="relative ml-auto">
              <div className="absolute left-[10px] top-1/2 -translate-y-1/2 text-[#999999]">
                <Search className="w-[14px] h-[14px]" />
              </div>
              <input 
                type="text" 
                value={faqSearchQuery}
                onChange={(e) => { setFaqSearchQuery(e.target.value); if (e.target.value) setActiveCategory('all'); }}
                placeholder="Search..."
                className={`rounded-[8px] pl-[30px] pr-[15px] py-[9px] text-[11px] font-medium focus:outline-none transition-all duration-[250ms] w-[160px] sm:w-[200px] ${
                  theme === 'light' 
                    ? 'bg-[#f5f5f5] border border-[#0f0f0f]/10 text-[#0f0f0f] placeholder-[#999999] focus:border-[#0f0f0f]/40' 
                    : 'bg-[#121212] border border-[#ffffff]/10 text-[#ffffff] placeholder-[#999999] focus:border-[#ffffff]/30'
                }`}
              />
              {faqSearchQuery && (
                <button onClick={() => setFaqSearchQuery('')} className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[10px] font-bold text-[#999999] hover:text-white cursor-pointer">✕</button>
              )}
            </div>
          </div>

          {/* FAQ CARDS LIST */}
          <div id="faq-questions-container" className="special-pages-policy-content py-[20px] space-y-0">
            {faqsToDisplay.map((item, idx) => {
              const isOpen = activeFaqId === item.id;
              return (
                <div 
                  key={item.id}
                  className={`transition-colors duration-[350ms] ${idx > 0 ? '' : ''}`}
                  style={{ borderBottom: `1px solid ${theme === 'light' ? 'rgba(15,15,15,0.08)' : 'rgba(255,255,255,0.08)'}` }}
                >
                  <button
                    onClick={() => setActiveFaqId(isOpen ? null : item.id)}
                    aria-expanded={isOpen}
                    className={`w-full text-left flex items-center justify-between gap-[15px] py-[20px] cursor-pointer select-none transition-colors duration-[250ms] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00ff85] ${
                      isOpen 
                        ? (theme === 'light' ? 'text-[#0f0f0f]' : 'text-[#ffffff]') 
                        : (theme === 'light' ? 'text-[#0f0f0f] hover:text-[#0f0f0f]/70' : 'text-[#999999] hover:text-[#ffffff]')
                    }`}
                  >
                    <span className="text-[14px] sm:text-[16px] font-semibold leading-[1.4] tracking-tight">{item.question}</span>
                    <span className={`text-[22px] font-light leading-none shrink-0 w-[25px] h-[25px] flex items-center justify-center rounded-full transition-all duration-[250ms] ${
                      isOpen 
                        ? theme === 'light' ? 'bg-[#0f0f0f] text-white rotate-45' : 'bg-[#ffffff] text-[#000000] rotate-45'
                        : theme === 'light' ? 'bg-[#0f0f0f]/5 text-[#0f0f0f]' : 'bg-[#ffffff]/5 text-[#999999]'
                    }`}>+</span>
                  </button>
                  
                  <div className={`transition-all duration-[350ms] ease-in-out overflow-hidden ${
                    isOpen ? 'max-h-[400px] opacity-100 pb-[20px]' : 'max-h-0 opacity-0'
                  }`}>
                    <p className="text-[13px] leading-[24.3px] text-[#999999] max-w-[720px]">
                      {item.answer}
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Empty state */}
            {faqsToDisplay.length === 0 && (
              <div className="text-center py-[25px] space-y-[10px]">
                <HelpCircle className="w-[25px] h-[25px] text-[#999999] mx-auto" />
                <p className="text-[13px] text-[#999999]">No questions match "{faqSearchQuery}"</p>
                <button onClick={() => setFaqSearchQuery('')} className={`px-[15px] py-[9px] rounded-[50px] text-[11px] font-bold cursor-pointer transition-all duration-[250ms] ${
                  theme === 'light' ? 'bg-[#0f0f0f] text-white' : 'bg-[#ffffff] text-[#000000]'
                }`}>Clear Search</button>
              </div>
            )}
          </div>

          {/* CONTACT CTA */}
          <div className={`mt-[15px] p-[20px] rounded-[8px] flex flex-col sm:flex-row items-center justify-between gap-[15px] transition-colors duration-[350ms] ${
            theme === 'light' ? 'bg-[#f5f5f5] border border-[#0f0f0f]/5' : 'bg-[#121212] border border-[#ffffff]/5'
          }`}>
            <div className="flex items-center gap-[15px] text-left">
              <div className={`w-[40px] h-[40px] rounded-[8px] flex items-center justify-center shrink-0 ${
                theme === 'light' ? 'bg-[#0f0f0f]/5' : 'bg-[#ffffff]/5'
              }`}>
                <HelpCircle className="w-[18px] h-[18px] text-[#999999]" />
              </div>
              <div>
                <h3 className={`text-[14px] font-bold tracking-tight ${theme === 'light' ? 'text-[#0f0f0f]' : 'text-[#ffffff]'}`}>
                  Still have a question?
                </h3>
                <p className="text-[11px] text-[#999999] mt-[3px]">
                  Reach out to our support team for assistance.
                </p>
              </div>
            </div>
            <a
              href="mailto:support@smartimagify.com"
              className={`px-[20px] py-[10px] rounded-[50px] text-[11px] font-bold tracking-wide uppercase transition-all duration-[250ms] border cursor-pointer select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00ff85] ${
                theme === 'light'
                  ? 'border-[#0f0f0f]/20 text-[#0f0f0f] hover:bg-[#0f0f0f] hover:text-white hover:border-[#0f0f0f]'
                  : 'border-[#ffffff]/10 text-[#ffffff] hover:bg-[#ffffff] hover:text-[#000000] hover:border-[#ffffff]'
              }`}
            >
              Contact Support
            </a>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // SITEMAP INDEX PAGE
  if (path === '/sitemap') {
    return (
      <div className={`w-full py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden font-sans transition-colors duration-300 ${
        theme === 'light' ? 'text-zinc-900 bg-white' : 'text-slate-100 bg-[#050507]'
      }`}>
        {/* Glow */}
        <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[300px] rounded-full blur-[130px] pointer-events-none -z-10 animate-pulse ${
          theme === 'light' ? 'bg-zinc-200/40' : 'bg-[#aace16]/5'
        }`} />
        
        <div className="max-w-6xl mx-auto space-y-12">
          {/* Header */}
          <div className="text-center space-y-4 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#aace16]/10 border border-[#aace16]/20 rounded-full text-xs font-black text-[#aace16] uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5" /> HTML Sitemap
            </div>
            <h1 className={`text-4xl md:text-5xl tracking-tight leading-none transition-colors ${
              theme === 'light' ? 'text-zinc-950' : 'text-white'
            }`}>
              <span className="font-extrabold">Sitemap</span> <span className="font-light text-zinc-400 dark:text-[#999999]">Index</span>
            </h1>
            <p className={`text-xs md:text-sm font-semibold transition-colors ${
              theme === 'light' ? 'text-zinc-500' : 'text-slate-400'
            }`}>
              Browse the complete directory index of public pages, local tools, and upcoming creative assets.
            </p>
          </div>

          {/* Sitemaps lists */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 pt-8">
            {[
              {
                title: "Public Landing Pages",
                links: [
                  { name: "Smart Imagify Home", href: "/" },
                  { name: "Subscription Pricing", href: "/pricing" },
                  { name: "Frequently Asked Questions", href: "/faq" },
                  { name: "About Mission & Founder", href: "/about" },
                  { name: "Contact Support Team", href: "/contact" },
                  { name: "Privacy Policy", href: "/privacy-policy" },
                  { name: "Terms of Service", href: "/terms-of-service" }
                ]
              },
              {
                title: "Creative Tools",
                links: [
                  { name: "Lossless Image Compressor", href: "/compress-image" },
                  { name: "Bulk ZIP Image Compressor", href: "/bulk-image-compressor" },
                  { name: "Creative Grid Collage Maker", href: "/collage-maker" },
                  { name: "DreamSeed AI Image Studio", href: "/ai-image-generator" },
                  { name: "Interactive Image Resizer", href: "/image-resizer" },
                  { name: "Image Format Converter", href: "/image-format-converter" },
                  { name: "High-Res Stock Search Library", href: "/stock-library" }
                ]
              },
              {
                title: "Upcoming AI Utilities",
                links: [
                  { name: "Background Remover AI", href: "/background-remover" },
                  { name: "Image Resolution Upscaler AI", href: "/image-upscaler" },
                  { name: "Watermark Remover Brush AI", href: "/watermark-remover" },
                  { name: "GPS coordinates Exif Scrubber", href: "/exif-scrubber" },
                  { name: "Image to PDF Document Export", href: "/image-to-pdf" },
                  { name: "PDF Document page to Image", href: "/pdf-to-image" },
                  { name: "GIF Frame Compressor tool", href: "/gif-compressor" },
                  { name: "Color Dropper Palette Matcher", href: "/color-picker" }
                ]
              },
              {
                title: "App User Workspace",
                links: [
                  { name: "Dashboard Workspace Studio", href: "/dashboard" },
                  { name: "Settings & Worker Parallelization", href: "/settings" },
                  { name: "Account Billing & Subscriptions", href: "/account" },
                  { name: "Creative Action History Logs", href: "/history" },
                  { name: "Invite Friends & Referral Credits", href: "/referral" }
                ]
              }
            ].map((group, idx) => (
              <div key={idx} className="space-y-4">
                <h3 className="text-xs font-mono font-black uppercase tracking-widest text-[#aace16] border-b border-[#aace16]/10 pb-2">
                  {group.title}
                </h3>
                <ul className="space-y-2.5 text-xs font-bold text-left">
                  {group.links.map((link, lIdx) => (
                    <li key={lIdx}>
                      <a 
                        href={link.href}
                        className="text-slate-400 hover:text-white transition-colors flex items-center gap-1.5"
                      >
                        <ChevronRight className="w-3 h-3 text-[#aace16]/60 shrink-0" />
                        <span>{link.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ABOUT US PAGE
  if (path === '/about' || path === '/about-us') {
    return (
      <PageWrapper title="About Smart Imagify" subtitle="The Story behind Ayshdan Innovations" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className="space-y-6 text-left text-xs sm:text-sm text-slate-300 leading-relaxed font-sans">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8 space-y-4">
              <h3 className="text-lg font-bold text-white">Reimagining Client-Side Creativity</h3>
              <p>
                Founded by **Muhammad Adnan**, Smart Imagify is a division of **AYSHDAN INNOVATIONS** dedicated to providing developers, WordPress administrators, designers, and regular users with super-fast, completely secure, and stunningly styled creative tools.
              </p>
              <p>
                We believe that simple web utilities don't need to be filled with intrusive ads, heavy server requirements, or complex registration flows. By engineering all mathematical compression pipelines locally inside client web browsers, we eliminate privacy concerns while maximizing local computing power.
              </p>
            </div>
            <div className="md:col-span-4 p-5 rounded-2xl bg-white/[0.02] border border-white/5 text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-[#aace16]/20 text-[#aace16] border border-[#aace16]/30 flex items-center justify-center font-extrabold mx-auto text-xl">
                MA
              </div>
              <div>
                <h4 className="font-extrabold text-white">Muhammad Adnan</h4>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Founder & Principal Architect</p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-white/[0.01] border border-white/5 space-y-1">
              <div className="text-[#aace16] font-bold text-lg">100% Private</div>
              <p className="text-xs text-slate-500">All processing is compiled offline. Your photos never touch our servers.</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.01] border border-white/5 space-y-1">
              <div className="text-[#aace16] font-bold text-lg">PageSpeed 95+</div>
              <p className="text-xs text-slate-500">Super optimized code footprint engineered for maximum desktop loading speeds.</p>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.01] border border-white/5 space-y-1">
              <div className="text-[#aace16] font-bold text-lg">GPU Powered</div>
              <p className="text-xs text-slate-500">Fluid 60fps animations utilizing hardware accelerated hardware transitions.</p>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // PREMIUM BLOG PAGE
  if (path === '/blog') {
    const getTagColor = (cat: string) => {
      if (theme === 'light') {
        switch (cat) {
          case 'Architecture': return 'bg-blue-50 text-blue-700 border-blue-200';
          case 'SEO Core Vitals': return 'bg-orange-50 text-orange-700 border-orange-200';
          case 'Development': return 'bg-purple-50 text-purple-700 border-purple-200';
          default: return 'bg-zinc-50 text-zinc-700 border-zinc-200';
        }
      } else {
        switch (cat) {
          case 'Architecture': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
          case 'SEO Core Vitals': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
          case 'Development': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
          default: return 'bg-white/5 text-slate-400 border-white/10';
        }
      }
    };

    return (
      <div className={`min-h-screen font-sans relative overflow-hidden transition-colors duration-300 ${
        theme === 'light' ? 'bg-white text-zinc-900' : 'bg-[#050507] text-slate-100'
      }`}>
        {/* Dynamic Background */}
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] rounded-full blur-[150px] pointer-events-none ${
          theme === 'light' ? 'bg-gradient-to-b from-zinc-200/40 via-zinc-100/20 to-transparent' : 'bg-gradient-to-b from-[#aace16]/10 via-[#aace16]/5 to-transparent'
        }`} />
        <div className={`absolute inset-0 pointer-events-none bg-[size:40px_40px] ${
          theme === 'light'
            ? 'bg-[linear-gradient(rgba(0,0,0,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.015)_1px,transparent_1px)]'
            : 'bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)]'
        }`} />

        <div className="max-w-[1200px] mx-auto px-6 py-20 relative z-10 space-y-16">
          {/* Header Navigation */}
          <button 
            onClick={onBackToHome}
            className={`group inline-flex items-center gap-2 px-4 py-2 rounded-xl border hover:border-[#aace16]/40 hover:bg-[#aace16]/10 text-xs font-bold transition-all cursor-pointer backdrop-blur-md ${
              theme === 'light'
                ? 'bg-zinc-50 border-zinc-250 text-zinc-800 hover:text-black hover:shadow-sm'
                : 'bg-white/5 border border-white/10 text-slate-300 hover:text-white'
            }`}
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1.5 transition-transform" />
            Return to Smart Imagify Studio
          </button>

          {/* Blog Hero Section */}
          <div className="space-y-6 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#aace16]/10 border border-[#aace16]/20 rounded-full text-xs font-black text-[#aace16] uppercase tracking-[0.2em] shadow-[0_0_20px_rgba(170,206,22,0.15)]">
              <Sparkles className="w-3.5 h-3.5" /> Official Resources
            </div>
            <h1 className={`text-5xl md:text-7xl tracking-tight leading-tight transition-colors ${
              theme === 'light' ? 'text-zinc-950' : 'text-white'
            }`}>
              <span className="font-extrabold">Insights &</span> <span className={theme === 'light' ? 'font-light text-zinc-400' : 'font-light text-[#999999]'}>Innovation</span>
            </h1>
            <p className={`text-base font-medium max-w-2xl mx-auto leading-relaxed transition-colors ${
              theme === 'light' ? 'text-zinc-500' : 'text-slate-400'
            }`}>
              Master web performance, discover advanced image compression techniques, and stay updated with Ayshdan Innovations' latest architectural breakthroughs.
            </p>
          </div>

          {/* Featured Article Banner */}
          <div className={`relative rounded-[2rem] overflow-hidden group cursor-pointer transition-all duration-500 ${
            theme === 'light'
              ? 'bg-zinc-50 border border-zinc-250 hover:border-[#aace16]/60 shadow-xl shadow-zinc-200/20'
              : 'bg-[#0c0c0f]/80 border border-white/10 hover:border-[#aace16]/30 shadow-2xl'
          }`}>
            <div className={`absolute inset-0 z-10 ${
              theme === 'light'
                ? 'bg-gradient-to-r from-zinc-50/95 via-zinc-50/85 to-transparent'
                : 'bg-gradient-to-r from-black via-black/80 to-transparent'
            }`} />
            <div className="absolute right-0 top-0 bottom-0 w-2/3 bg-gradient-to-tr from-[#aace16]/20 to-emerald-500/20 opacity-50 group-hover:opacity-80 group-hover:scale-105 transition-all duration-1000" />
            <img 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2000" 
              className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-1000"
              alt="Featured"
            />
            
            <div className="relative z-20 p-8 md:p-16 flex flex-col justify-end min-h-[400px] w-full md:w-2/3 space-y-6">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded bg-[#aace16] text-black text-[10px] font-black uppercase tracking-widest">Featured</span>
                <span className={`text-xs font-mono ${theme === 'light' ? 'text-zinc-500' : 'text-slate-300'}`}>May 18, 2026 • 8 min read</span>
              </div>
              <h2 className={`text-3xl md:text-5xl font-black leading-tight transition-colors ${
                theme === 'light' ? 'text-zinc-950 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'
              }`}>
                How to Hit 100/100 PageSpeed on Shopify & WordPress
              </h2>
              <p className={`text-sm md:text-base leading-relaxed font-medium ${
                theme === 'light' ? 'text-zinc-650' : 'text-slate-300'
              }`}>
                Unoptimized images represent over 70% of average website payload weights. Discover how batch converting standard JPGs into next-gen AVIF/WebP formats cuts initial loading times under 1 second, instantly boosting your Google SERP authority without touching complex server architectures.
              </p>
              
              <div className="flex items-center gap-4 pt-4">
                <div className="w-10 h-10 rounded-full bg-[#aace16]/20 border border-[#aace16]/40 flex items-center justify-center text-[#aace16] font-black">
                  MA
                </div>
                <div>
                  <div className={`text-sm font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Muhammad Adnan</div>
                  <div className={`text-[10px] font-mono uppercase tracking-widest ${theme === 'light' ? 'text-zinc-400' : 'text-slate-405'}`}>Lead Engineer</div>
                </div>
              </div>
            </div>
          </div>

          {/* Grid Layout for Older Posts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                category: 'Architecture',
                title: "AVIF vs WebP vs JPG: The Ultimate 2026 Image Compression Guide",
                desc: "Understand alpha transparency channels, lossless colors thresholds, and exact byte optimization limits between formats. Find out why AVIF is dominating the modern web stack.",
                date: "May 10, 2026",
                author: "Ayshdan Team",
                read: "5 min",
                color: "from-blue-500/20 to-cyan-500/5",
              },
              {
                category: 'SEO Core Vitals',
                title: "Eliminating Cumulative Layout Shift (CLS) on Complex Image Grids",
                desc: "Google’s Core Web Vitals heavily penalize layouts that jump during load cycles. Master the art of using strict CSS aspect-ratios to guarantee flawless 0.00 CLS scores.",
                date: "April 28, 2026",
                author: "Muhammad Adnan",
                read: "12 min",
                color: "from-orange-500/20 to-red-500/5",
              },
              {
                category: 'Development',
                title: "Client-Side Processing: Why We Abandoned Cloud Servers",
                desc: "A deep dive into WebAssembly, local sandboxing, and why shifting heavy image computational processing directly to the user's browser guarantees infinite scalability and 100% privacy.",
                date: "April 15, 2026",
                author: "Ayshdan Team",
                read: "9 min",
                color: "from-purple-500/20 to-pink-500/5",
              }
            ].map((post, idx) => (
              <div key={idx} className={`group relative rounded-3xl transition-all duration-300 overflow-hidden cursor-pointer flex flex-col h-full ${
                theme === 'light'
                  ? 'bg-white border border-zinc-200 hover:border-[#aace16]/60 shadow-md hover:shadow-xl hover:shadow-zinc-200/10'
                  : 'bg-[#0c0c0f]/80 border border-white/10 hover:border-white/30 shadow-lg hover:shadow-2xl'
              }`}>
                {/* Visual Header */}
                <div className={`h-40 w-full bg-gradient-to-br ${post.color} relative overflow-hidden flex items-center justify-center border-b ${
                  theme === 'light' ? 'border-zinc-150' : 'border-white/5'
                }`}>
                  <BookOpen className={`w-12 h-12 group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 ${
                    theme === 'light' ? 'text-zinc-400/40' : 'text-white/20'
                  }`} />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                
                {/* Content Body */}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-2.5 py-1 rounded-md border text-[9px] font-black uppercase tracking-wider ${getTagColor(post.category)}`}>
                      {post.category}
                    </span>
                    <span className={`text-[10px] font-mono ${theme === 'light' ? 'text-zinc-400' : 'text-slate-500'}`}>{post.read} read</span>
                  </div>
                  
                  <h3 className={`text-xl font-black transition-colors leading-snug mb-3 ${
                    theme === 'light' ? 'text-zinc-950 group-hover:text-[#aace16]' : 'text-white group-hover:text-[#aace16]'
                  }`}>
                    {post.title}
                  </h3>
                  
                  <p className={`text-xs leading-relaxed flex-1 ${theme === 'light' ? 'text-zinc-550' : 'text-slate-400'}`}>
                    {post.desc}
                  </p>
                  
                  <div className={`mt-6 pt-5 border-t flex items-center justify-between ${
                    theme === 'light' ? 'border-zinc-100' : 'border-white/5'
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold ${
                        theme === 'light' ? 'bg-zinc-100 text-zinc-600' : 'bg-white/10 text-slate-300'
                      }`}>
                        {post.author.substring(0, 2).toUpperCase()}
                      </div>
                      <span className={`text-[10px] font-bold ${theme === 'light' ? 'text-zinc-700' : 'text-slate-300'}`}>{post.author}</span>
                    </div>
                    <span className={`text-[10px] font-mono ${theme === 'light' ? 'text-zinc-400' : 'text-slate-500'}`}>{post.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Newsletter / Notification Block */}
          <div className="mt-20 p-8 md:p-12 rounded-[2rem] bg-gradient-to-r from-[#aace16] to-[#bdef2b] relative overflow-hidden shadow-[0_20px_50px_rgba(170,206,22,0.2)]">
            <div className="absolute right-0 top-0 w-64 h-full bg-black/5 -skew-x-12 transform origin-top pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="space-y-2 text-black text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-black tracking-tight">Never Miss an Update</h3>
                <p className="text-sm font-semibold opacity-80 max-w-md">Subscribe to get the latest web performance tutorials and Smart Imagify tool announcements directly to your inbox.</p>
              </div>
              <div className="w-full md:w-auto flex gap-2">
                <input 
                  type="email" 
                  placeholder="name@company.com" 
                  className="w-full md:w-64 px-5 py-4 bg-black/10 border border-black/20 rounded-2xl text-black placeholder:text-black/60 font-bold focus:outline-none focus:ring-2 focus:ring-black/20 transition-all"
                />
                <button className="px-6 py-4 bg-black text-white hover:bg-slate-800 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-xl shadow-black/20 shrink-0">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CONTACT US PAGE
  if (path === '/contact' || path === '/contact-us') {
    return (
      <PageWrapper title="Contact Support" subtitle="Get in touch with AYSHDAN INNOVATIONS for help & licensing queries" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 text-left">
          <div className="md:col-span-7 space-y-6">
            <h3 className={`text-lg font-bold ${theme === 'light' ? 'text-zinc-950' : 'text-white'}`}>Send Us a Message</h3>
            
            {contactSubmitted ? (
              <div className="p-5 bg-[#aace16]/15 border border-[#aace16]/30 text-[#aace16] rounded-2xl text-xs font-bold text-center space-y-2 animate-fade-in-up">
                <Sparkles className="w-8 h-8 text-[#aace16] mx-auto animate-pulse" />
                <h4 className="text-sm font-extrabold">Message Logged!</h4>
                <p className={`text-[11px] ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Muhammad Adnan's support team will trace back relative queries inside 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4 font-sans text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className={`font-bold uppercase tracking-wider text-[10px] ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Your Name</label>
                    <input 
                      type="text" 
                      required
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="Muhammad Adnan"
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-[#aace16] focus:ring-1 focus:ring-[#aace16] transition-all font-semibold ${
                        theme === 'light'
                          ? 'bg-white border-zinc-250 text-zinc-900 placeholder:text-zinc-400'
                          : 'bg-white/5 border border-white/10 text-slate-200 placeholder:text-slate-650'
                      }`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={`font-bold uppercase tracking-wider text-[10px] ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Email Address</label>
                    <input 
                      type="email" 
                      required
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="adnan@ayshdan.com"
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-[#aace16] focus:ring-1 focus:ring-[#aace16] transition-all font-semibold ${
                        theme === 'light'
                          ? 'bg-white border-zinc-250 text-zinc-900 placeholder:text-zinc-400'
                          : 'bg-white/5 border border-white/10 text-slate-200 placeholder:text-slate-650'
                      }`}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={`font-bold uppercase tracking-wider text-[10px] ${theme === 'light' ? 'text-zinc-500' : 'text-slate-400'}`}>Message Details</label>
                  <textarea 
                    required
                    rows={4}
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Write details relative to corporate API queries, bug issues, or custom premium requirements..."
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-[#aace16] focus:ring-1 focus:ring-[#aace16] transition-all font-semibold resize-none ${
                      theme === 'light'
                        ? 'bg-white border-zinc-250 text-zinc-900 placeholder:text-zinc-400'
                        : 'bg-white/5 border border-white/10 text-slate-200 placeholder:text-slate-650'
                    }`}
                  />
                </div>

                <button 
                  type="submit"
                  className={`w-full py-3.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer ${
                    theme === 'light'
                      ? 'bg-zinc-950 text-white hover:bg-zinc-800 shadow-sm'
                      : 'bg-white text-black hover:bg-slate-200'
                  }`}
                >
                  <span>Submit Ticket</span>
                  <SendHorizontal className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </div>

          <div className="md:col-span-5 space-y-6">
            <h3 className={`text-lg font-bold ${theme === 'light' ? 'text-zinc-950' : 'text-white'}`}>Office Specs</h3>
            
            <div className={`p-5 rounded-2xl border space-y-4 text-xs ${
              theme === 'light' ? 'bg-zinc-50 border-zinc-200 text-zinc-600' : 'bg-white/[0.02] border border-white/5 text-slate-400'
            }`}>
              <div className="space-y-1">
                <h4 className={`font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Lead Development Banner</h4>
                <p className="font-semibold text-[#aace16]">AYSHDAN INNOVATIONS</p>
              </div>

              <div className="space-y-1">
                <h4 className={`font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Corporate Support Email</h4>
                <a href="mailto:support@smartimagify.com" className={`underline ${theme === 'light' ? 'text-zinc-700 hover:text-zinc-950' : 'text-slate-300 hover:text-white'}`}>support@smartimagify.com</a>
              </div>

              <div className="space-y-1">
                <h4 className={`font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>Developer Website</h4>
                <a href="https://www.ayshdan.com" target="_blank" rel="noreferrer" className={`underline ${theme === 'light' ? 'text-zinc-700 hover:text-zinc-950' : 'text-slate-300 hover:text-white'}`}>www.ayshdan.com</a>
              </div>
              
              <div className={`border-t pt-3 leading-relaxed ${theme === 'light' ? 'border-zinc-200' : 'border-white/5'}`}>
                We generally review ticket items and support queries within 12-24 hours. For quick answers relative to API structures, please view our FAQ section.
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // COMING SOON PAGES RENDERING

  if (path === '/background-remover') {
    return (
      <ComingSoonWrapper 
        title="Background Remover Online | Free AI BG Eraser Tool"
        keywords={['background-remover-online', 'remove-background-from-image', 'ai-bg-remover']}
        icon={Layers}
        mockUpTitle="Background Removal Engine"
        mockUpDescription="Simulate alpha channel separation on portrait sample files with instant preview filters."
        features={[
          'Instant portrait background deletion',
          'Intelligent product photo hair isolation',
          'Export as transparent PNG or WebP',
          'Zero quality loss or scaling changes'
        ]}
        description="Our upcoming AI Background Remover leverages specialized local neural networks running in your browser thread. Strip away backgrounds from portraits, e-commerce products, and landscape shots inside 100ms. Keep fine hair details, shadows, and alpha channel highlights intact without sending a single byte of your valuable photo archives across remote servers."
      />
    );
  }

  if (path === '/image-upscaler') {
    return (
      <ComingSoonWrapper 
        title="AI Image Upscaler | Enhance Resolution 4x Free Online"
        keywords={['ai-image-upscaler', 'increase-image-resolution', 'enhance-image-quality']}
        icon={Sparkles}
        mockUpTitle="Super Resolution Upscaler"
        mockUpDescription="Upscale low-resolution previews up to 400% while hallucinating hyper-realistic details locally."
        features={[
          'Increase image dimensions up to 4x',
          'Denoise JPG compression artifacts',
          'Restore face features in old photos',
          'High resolution output under 50KB constraints'
        ]}
        description="The AI Image Upscaler utilizes next-generation super-resolution neural architectures executing completely within your browser's WebGL context. Say goodbye to blurry pixels and generic scaling filters. Hallucinate realistic textures, enhance camera shot highlights, and enlarge low-resolution vector graphics up to 400% for high fidelity print production entirely offline."
      />
    );
  }

  if (path === '/watermark-remover') {
    return (
      <ComingSoonWrapper 
        title="Watermark Remover Online | Clean Images for Free"
        keywords={['watermark-remover-online', 'remove-watermark-from-image']}
        icon={Lock}
        mockUpTitle="Inpainting Eraser Tool"
        mockUpDescription="Highlight text or logos and watch them disappear as smart patch generation restores backgrounds."
        features={[
          'Smart textual watermark inpainting',
          'Semi-transparent logo removals',
          'Custom patch restoration parameters',
          'Zero local artifact generation'
        ]}
        description="Strip away annoying overlays, text stamps, and copyright logos with our local inpainting model. The Watermark Remover analyzes the texture boundary surrounding target highlights, seamlessly recreating background details to leave a clean, professional, and unblemished photo file in milliseconds without server compression."
      />
    );
  }

  if (path === '/image-to-pdf') {
    return (
      <ComingSoonWrapper 
        title="Image to PDF Converter | JPG PNG to PDF Online Free"
        keywords={['image-to-pdf', 'jpg-to-pdf-online', 'convert-image-to-pdf']}
        icon={FileText}
        mockUpTitle="PDF Binder Module"
        mockUpDescription="Drag multiple photos, arrange pages, and compile a single high-quality PDF document locally."
        features={[
          'Combine JPG, PNG, WebP into PDF',
          'Adjust page margins and formats',
          'Strict local data confidentiality',
          'Export highly compressed small PDFs'
        ]}
        description="Convert single or batch image portfolios into highly clean PDF documents. Perfect for job application forms, visa passport paperwork, and custom graphic portfolios. Drag multiple assets, lock custom aspect ratios, align margins, and download the compiled PDF locally in your browser instantly."
      />
    );
  }

  if (path === '/pdf-to-image') {
    return (
      <ComingSoonWrapper 
        title="PDF to Image Converter | Extract Pages Free Online"
        keywords={['pdf-to-image-converter']}
        icon={FileText}
        mockUpTitle="PDF Page Extractor"
        mockUpDescription="Upload a PDF file and instantly render individual pages as high-resolution PNG photos locally."
        features={[
          'Extract all pages as PNG or JPG',
          'Render high-fidelity PDF vectors',
          'Complete offline data confidentiality',
          'Export specific target pages in batch'
        ]}
        description="The PDF to Image Converter extracts and renders individual pages from your PDF documents into high-resolution JPG or PNG formats locally. No cloud uploads mean your sensitive corporate PDF contracts and financial ledgers are kept 100% private in your own device's sandboxed environment."
      />
    );
  }

  if (path === '/compress-gif') {
    return (
      <ComingSoonWrapper 
        title="GIF Compressor | Reduce GIF File Size Online Free"
        keywords={['compress-gif-online', 'gif-compressor', 'reduce-gif-size']}
        icon={Cpu}
        mockUpTitle="Lzw GIF Optimizer"
        mockUpDescription="Dither, scale, and adjust LZW color indexing to compress bulky GIF animations locally."
        features={[
          'Optimized LZW frame indexing',
          'Dither color reduction options',
          'Scale frame rates for fast loads',
          'Maintains animation loops intact'
        ]}
        description="Bulky GIF animations are notorious PageSpeed ranking killers. Our GIF Compressor scans redundant pixels across individual timeline frames, dithering colors and scaling dimensions to reduce file sizes up to 80% without introducing noticeable loop lag. Accelerate your SEO loading authority instantly."
      />
    );
  }

  if (path === '/image-color-picker') {
    return (
      <ComingSoonWrapper 
        title="Image Color Picker | Extract HEX RGB Colors Online"
        keywords={['color-picker-from-image']}
        icon={Globe}
        mockUpTitle="Palette Dropper Module"
        mockUpDescription="Hover over any pixel in your image to reveal and copy precise HEX, RGB, HSL values locally."
        features={[
          'Interactive target color dropper',
          'Auto-generate matching palettes',
          'Support HEX, RGB, HSL values',
          'Zero image uploads to server'
        ]}
        description="Upload any photo to extract precise HSL, HEX, and RGB parameters from specific pixels. Our Interactive Color Picker builds dynamic color schemes and palette guides instantly, making it highly valuable for UI designers, branding builders, and developers drafting site design layouts."
      />
    );
  }

  if (path === '/exif-remover') {
    return (
      <ComingSoonWrapper 
        title="EXIF Data Remover | Strip Image Metadata Free"
        keywords={['remove-exif-data-online']}
        icon={Shield}
        mockUpTitle="EXIF Tag Scrubber"
        mockUpDescription="Scan and strip GPS coordinates, camera models, and private author metadata with one click."
        features={[
          'Scrub GPS lat/long location coordinates',
          'Strip camera specifications & dates',
          'Batch scrub metadata from folders',
          'Complete offline browser security'
        ]}
        description="Secure your online privacy by scrubbing hidden metadata, GPS location parameters, capture timestamps, and hardware specifications embedded inside mobile photos. Our EXIF Scrubber removes metadata locally in a fraction of a second, ensuring your shared graphics contain only pure pixels."
      />
    );
  }

  // DEDICATED TOOLS DIRECTORY PAGE
  if (path === '/tools') {

    const toolsCatalog = [
      {
        id: 'compress',
        name: 'Lossless Image Compressor',
        desc: 'Reduce file size up to 90% in milliseconds. Processes PNG, JPG, and WebP using local WebAssembly threads with zero quality loss.',
        category: 'compression',
        badge: 'WebAssembly',
        status: 'active',
        path: '/compress-image',
        icon: ImageIcon
      },
      {
        id: 'bulk',
        name: 'Bulk ZIP Compressor',
        desc: 'Select hundreds of images, run parallel batch compression in multi-core web worker pools, and download an organized ZIP archive instantly.',
        category: 'compression',
        badge: 'Multi-Threaded',
        status: 'active',
        path: '/bulk-image-compressor',
        icon: Layers
      },
      {
        id: 'collage',
        name: 'Creative Grid Collage Maker',
        desc: 'Stitch images together into stunning grid compositions. Choose from 150+ geometric presets with customizable borders and gaps.',
        category: 'editing',
        badge: 'Interactive Layouts',
        status: 'active',
        path: '/collage-maker',
        icon: LayoutGrid
      },
      {
        id: 'ai',
        name: 'DreamSeed AI Image Studio',
        desc: 'Transform descriptive prompts into high-resolution, commercial-grade digital artwork using browser-optimized text-to-image models.',
        category: 'generation',
        badge: 'DreamSeed v4.2',
        status: 'active',
        path: '/ai-image-generator',
        icon: Sparkles
      },
      {
        id: 'resize',
        name: 'Interactive Image Resizer',
        desc: 'Scale, crop, and morph photo aspect ratios to social media dimensions. Uses lanczos resampling filters for crisp borders.',
        category: 'optimization',
        badge: 'Lanczos-3 Resampler',
        status: 'active',
        path: '/image-resizer',
        icon: Maximize
      },
      {
        id: 'convert',
        name: 'Image Format Converter',
        desc: 'Transcode images between next-gen formats WebP, AVIF, PNG, and JPG. Preserves alpha channels and limits color space compression.',
        category: 'conversion',
        badge: 'Lossless Conversion',
        status: 'active',
        path: '/image-format-converter',
        icon: RefreshCw
      },
      {
        id: 'stock',
        name: 'High-Res Stock Search Library',
        desc: 'Search, discover, and download millions of royalty-free commercial stock photographs. Direct insertion into other creative tools.',
        category: 'utilities',
        badge: 'API Connected',
        status: 'active',
        path: '/stock-library',
        icon: Search
      },
      {
        id: 'bg-remover',
        name: 'Background Remover AI',
        desc: 'Erase background elements from portrait or e-commerce products in 100ms. Uses local smart image matting networks.',
        category: 'generation',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/background-remover',
        icon: Shield
      },
      {
        id: 'upscaler',
        name: 'AI Image Resolution Upscaler',
        desc: 'Enhance low-resolution graphics up to 400% with hyper-realistic texture detail hallucination running in WebGL context.',
        category: 'optimization',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/image-upscaler',
        icon: Cpu
      },
      {
        id: 'watermark',
        name: 'Watermark Remover AI Brush',
        desc: 'Scrub textual watermarks, timestamps, or logo overlays. Inpainting model fills background textures seamlessly.',
        category: 'editing',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/watermark-remover',
        icon: Lock
      },
      {
        id: 'exif',
        name: 'EXIF Metadata Data Scrubber',
        desc: 'Strip GPS coordinates, camera models, capture dates, and private tags from graphic metadata locally before sharing online.',
        category: 'utilities',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/exif-remover',
        icon: Eye
      },
      {
        id: 'to-pdf',
        name: 'Image to PDF Binder Converter',
        desc: 'Compile PNG, JPG, and WebP assets into a single highly optimized PDF document. Setup margins, page breaks, and scale constraints.',
        category: 'conversion',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/image-to-pdf',
        icon: FileText
      },
      {
        id: 'pdf-to-img',
        name: 'PDF Page to Image Extractor',
        desc: 'Convert PDF documents to image formats locally in browser. Extract pages as high-resolution PNG or JPG files for free.',
        category: 'conversion',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/pdf-to-image',
        icon: FileText
      },
      {
        id: 'gif-compress',
        name: 'GIF Frame Rate Compressor',
        desc: 'Compress bulky animated loop payload sizes up to 80% by optimizing dither schemes and LZW indexing tables locally.',
        category: 'compression',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/compress-gif',
        icon: Zap
      },
      {
        id: 'color-picker',
        name: 'Image Dropper Color Picker',
        desc: 'Extract exact HEX, RGB, and HSL parameters from pixels on an interactive canvas. Builds dynamic CSS palette codes.',
        category: 'utilities',
        badge: 'Coming Soon',
        status: 'soon',
        path: '/image-color-picker',
        icon: Globe
      }
    ];

    const filteredTools = toolsCatalog.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(toolsSearchQuery.toLowerCase()) || 
                            tool.desc.toLowerCase().includes(toolsSearchQuery.toLowerCase());
      
      const matchesCategory = toolsActiveTab === 'all' || 
                              tool.category === toolsActiveTab ||
                              (toolsActiveTab === 'compression' && tool.category === 'compression') ||
                              (toolsActiveTab === 'optimization' && (tool.category === 'optimization' || tool.category === 'compression'));
      
      return matchesSearch && matchesCategory;
    });

    return (
      <div className={`w-full py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden font-sans transition-colors duration-300 ${
        theme === 'light' ? 'text-zinc-900 bg-white' : 'text-slate-100 bg-[#050507]'
      }`}>
        {/* Ambient Grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none -z-10" />
        
        {/* Glow */}
        <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[300px] rounded-full blur-[130px] pointer-events-none -z-10 animate-pulse ${
          theme === 'light' ? 'bg-zinc-200/40' : 'bg-[#aace16]/5'
        }`} />

        <div className="max-w-7xl mx-auto space-y-12">
          {/* Header */}
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#aace16]/10 border border-[#aace16]/20 rounded-full text-xs font-black text-[#aace16] uppercase tracking-wider">
              <LayoutGrid className="w-3.5 h-3.5" /> Creative Suite Directory
            </div>
            <h1 className={`text-4xl md:text-6xl tracking-tight leading-none transition-colors ${
              theme === 'light' ? 'text-zinc-950' : 'text-white'
            }`}>
              <span className="font-extrabold">All-in-One</span> <span className="font-light text-zinc-400 dark:text-[#999999]">Image Studio Directory</span>
            </h1>
            <p className={`text-xs md:text-sm font-semibold max-w-2xl mx-auto leading-relaxed transition-colors ${
              theme === 'light' ? 'text-zinc-500' : 'text-slate-400'
            }`}>
              Explore 15 state-of-the-art client-side utilities and neural network graphic generators. Everything operates securely inside your system memory without remote server uploads.
            </p>
          </div>

          {/* Filtering controls and Search */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-[#0c0c0f]/90 border border-white/5 p-4 rounded-[2rem] max-w-5xl mx-auto backdrop-blur-xl">
            {/* Category Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { id: 'all', label: 'All Studio' },
                { id: 'generation', label: 'AI Generation' },
                { id: 'compression', label: 'Compression' },
                { id: 'optimization', label: 'Optimization' },
                { id: 'conversion', label: 'Conversion' },
                { id: 'editing', label: 'Editing' },
                { id: 'utilities', label: 'Utilities' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setToolsActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
                    toolsActiveTab === tab.id
                      ? 'bg-[#aace16] text-black shadow-lg shadow-[#aace16]/10'
                      : 'bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white border border-white/5'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-64">
              <Search className="w-4 h-4 text-slate-500 absolute left-4.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={toolsSearchQuery}
                onChange={(e) => setToolsSearchQuery(e.target.value)}
                placeholder="Search creative tools..."
                className="w-full pl-11 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-xl text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-[#aace16] transition-all font-semibold"
              />
              {toolsSearchQuery && (
                <button 
                  onClick={() => setToolsSearchQuery('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {filteredTools.length > 0 ? (
              filteredTools.map((tool) => {
                const ToolIcon = tool.icon;
                return (
                  <div
                    key={tool.id}
                    onClick={() => triggerRoute(tool.path)}
                    className="group relative bg-[#0c0c0f]/80 border border-white/5 hover:border-white/20 p-6 rounded-[2rem] flex flex-col justify-between h-64 transition-all duration-300 hover:shadow-xl hover:shadow-[#aace16]/5 cursor-pointer overflow-hidden text-left"
                  >
                    {/* Spotlight Glow Effect on Hover */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_var(--x,_50%)_var(--y,_50%),rgba(170,206,22,0.05)_0%,transparent_50%)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                      onMouseMove={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;
                        e.currentTarget.style.setProperty('--x', `${x}px`);
                        e.currentTarget.style.setProperty('--y', `${y}px`);
                      }}
                    />

                    <div className="space-y-4 relative z-10">
                      <div className="flex items-center justify-between">
                        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 group-hover:text-[#aace16] group-hover:border-[#aace16]/30 transition-all">
                          <ToolIcon className="w-5 h-5" />
                        </div>
                        <span className={`text-[9px] font-mono font-black uppercase px-2.5 py-1 rounded-md border ${
                          tool.status === 'active' 
                            ? 'bg-[#aace16]/10 text-[#aace16] border-[#aace16]/20' 
                            : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                        }`}>
                          {tool.badge}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <h3 className="text-sm font-black uppercase text-white group-hover:text-[#aace16] transition-colors leading-tight">
                          {tool.name}
                        </h3>
                        <p className="text-xs text-slate-400 font-semibold leading-relaxed line-clamp-3">
                          {tool.desc}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/5 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider font-black text-slate-500 group-hover:text-white transition-colors relative z-10">
                      <span>{tool.status === 'active' ? 'Launch Local App' : 'Request Early Access'}</span>
                      <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1.5 transition-transform" />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full py-16 text-center text-slate-500 space-y-4">
                <p className="font-semibold text-sm">No creative utilities found matching your query.</p>
                <button 
                  onClick={() => { setToolsSearchQuery(''); setToolsActiveTab('all'); }} 
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 rounded-xl text-xs font-black uppercase"
                >
                  Reset Catalog filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DEDICATED DASHBOARD PREVIEW
  if (path === '/dashboard-preview') {

    const startSimulateCompress = () => {
      setSimulateProgress(0);
      setSimulateActive(true);
      setSimulatedSize(null);
      setSimulatedTime(null);
      
      const interval = setInterval(() => {
        setSimulateProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              setSimulatedSize('92% Saved (4.8MB -> 384KB)');
              setSimulatedTime('4.8ms compilation time');
              setCredits((c) => Math.max(0, c - 1));
              setSimulateActive(false);
              addToast('success', 'Compression Sandbox Completed Locally! 🚀');
            }, 300);
            return 100;
          }
          return prev + 10;
        });
      }, 80);
    };

    const startSimulateAI = () => {
      if (!aiPrompt.trim()) return;
      setSimulateProgress(0);
      setSimulateActive(true);
      setAiArtwork(null);
      setAiLogs([
        'Initializing local generator node...',
        'Compiling DreamSeed v4.2 model parameters...',
        'Injecting custom seed token 98127391...'
      ]);

      const logSteps = [
        'Running 45 denoising scheduling loops...',
        'Applying octane render neural filters...',
        'Formatting image viewport dimensions to 1024x1024...',
        'Local graphics pipeline completed successfully.'
      ];

      let currentStepIndex = 0;
      const interval = setInterval(() => {
        setSimulateProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              // Show mock generated image
              setAiArtwork('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&auto=format&fit=crop&q=80');
              setCredits((c) => Math.max(0, c - 2));
              setSimulateActive(false);
              addToast('success', 'DreamSeed AI Generation Complete! 🎨');
            }, 300);
            return 100;
          }
          
          if (prev % 25 === 0 && currentStepIndex < logSteps.length) {
            setAiLogs(l => [...l, logSteps[currentStepIndex]]);
            currentStepIndex++;
          }
          
          return prev + 5;
        });
      }, 100);
    };

    return (
      <div className="text-slate-100 font-sans bg-[#000000] w-full py-12 px-4 sm:px-6 relative overflow-hidden">
        {/* Glow */}
        <div className="absolute top-1/4 left-1/4 -translate-y-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#aace16]/5 rounded-full blur-[140px] pointer-events-none -z-10 animate-pulse" />
        <div className="absolute top-1/3 right-1/4 -translate-y-1/2 translate-x-1/2 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[140px] pointer-events-none -z-10" />

        <div className="max-w-7xl mx-auto space-y-10 text-left">
          {/* Header */}
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#aace16]/10 border border-[#aace16]/20 rounded-full text-[10px] font-black text-[#aace16] uppercase tracking-wider">
              <Eye className="w-3.5 h-3.5" /> Interactive Sandbox Preview
            </div>
            <h1 className="text-3xl md:text-5xl tracking-tight text-white leading-none">
              <span className="font-extrabold">Futuristic SaaS</span> <span className="font-light text-zinc-400 dark:text-[#999999]">Studio Workspace</span>
            </h1>
            <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-3xl leading-relaxed">
              Test drive the Smart Imagify Studio interface. Experience the lightning fast local compression speeds and play with our AI prompting interface in the interactive sandbox below.
            </p>
          </div>

          {/* Full Mock Dashboard Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-[#09090b] border border-white/5 rounded-[2.5rem] p-4 md:p-6 shadow-2xl relative overflow-hidden min-h-[600px]">
            {/* Ambient Background Grid inside mock */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:30px_30px] pointer-events-none" />

            {/* Sidebar Column (3 cols) */}
            <div className="lg:col-span-3 space-y-6 border-b lg:border-b-0 lg:border-r border-white/5 pb-6 lg:pb-0 lg:pr-6 relative z-10 flex flex-col justify-between min-h-[400px] lg:min-h-[500px]">
              <div className="space-y-6">
                {/* Branding title */}
                <div className="flex items-center gap-2 px-2">
                  <div className="w-5 h-5 rounded-lg bg-[#aace16] flex items-center justify-center text-black font-black text-xs">S</div>
                  <span className="text-xs font-black uppercase tracking-[0.2em] text-white">Imagify Studio</span>
                </div>

                {/* Tabs */}
                <nav className="space-y-1.5 text-xs font-bold text-slate-400">
                  <div className="px-3 py-2.5 rounded-xl bg-white/5 text-white flex items-center gap-2.5 border border-white/5">
                    <Sparkles className="w-4 h-4 text-[#aace16]" />
                    <span>Creative Sandbox</span>
                  </div>
                  <div className="px-3 py-2.5 rounded-xl hover:bg-white/5 hover:text-white transition-all flex items-center gap-2.5 cursor-not-allowed opacity-50">
                    <Layers className="w-4 h-4" />
                    <span>Bulk Optimizers</span>
                  </div>
                  <div className="px-3 py-2.5 rounded-xl hover:bg-white/5 hover:text-white transition-all flex items-center gap-2.5 cursor-not-allowed opacity-50">
                    <LayoutGrid className="w-4 h-4" />
                    <span>Collage Studio</span>
                  </div>
                  <div className="px-3 py-2.5 rounded-xl hover:bg-white/5 hover:text-white transition-all flex items-center gap-2.5 cursor-not-allowed opacity-50">
                    <Eye className="w-4 h-4" />
                    <span>History Logs</span>
                  </div>
                  <div className="px-3 py-2.5 rounded-xl hover:bg-white/5 hover:text-white transition-all flex items-center gap-2.5 cursor-not-allowed opacity-50">
                    <Cpu className="w-4 h-4" />
                    <span>System Settings</span>
                  </div>
                </nav>
              </div>

              {/* Credit Status Panel */}
              <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3 font-sans text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-bold">Sandbox Credits</span>
                  <span className="text-white font-mono font-black">{credits} / 1,000</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#aace16] to-[#bdef2b] transition-all duration-300" style={{ width: `${(credits / 1000) * 100}%` }} />
                </div>
                <button 
                  onClick={() => triggerRoute('/pricing')}
                  className="w-full py-2 bg-[#aace16] hover:bg-[#bdef2b] text-black text-[10px] font-black uppercase tracking-wider rounded-lg transition-colors cursor-pointer text-center"
                >
                  Upgrade to Creative Pro
                </button>
              </div>
            </div>

            {/* Main Interactive Sandbox Content (9 cols) */}
            <div className="lg:col-span-9 space-y-6 lg:pl-6 relative z-10 flex flex-col justify-between">
              {/* Sandbox Control Header */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-white/5 pb-4">
                <div className="space-y-1">
                  <h3 className="text-base font-black uppercase text-white tracking-tight flex items-center gap-1.5">
                    Studio Sandbox Engine <span className="text-[9px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-mono">100% OFFLINE</span>
                  </h3>
                  <p className="text-[10px] text-slate-500 font-semibold">Simulate heavy graphic operations locally within client RAM cache buffers.</p>
                </div>

                {/* Sandbox Toggle Mode tabs */}
                <div className="flex items-center gap-1 bg-black/40 border border-white/10 p-1 rounded-xl">
                  <button
                    onClick={() => { setSimulateMode('compress'); setSimulateActive(false); setSimulateProgress(0); }}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-colors cursor-pointer ${
                      simulateMode === 'compress' ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    Local Compressor
                  </button>
                  <button
                    onClick={() => { setSimulateMode('ai'); setSimulateActive(false); setSimulateProgress(0); }}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-colors cursor-pointer ${
                      simulateMode === 'ai' ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    AI Generator
                  </button>
                </div>
              </div>

              {/* Sandbox Body Content */}
              <div className="flex-1 py-4">
                {simulateMode === 'compress' ? (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    {/* Left: Input upload simulator */}
                    <div className="md:col-span-7 space-y-6">
                      <div className="border border-dashed border-white/10 hover:border-[#aace16]/30 rounded-2xl p-8 text-center bg-white/[0.01] transition-all relative">
                        <ImageIcon className="w-8 h-8 text-[#aace16]/60 mx-auto mb-3" />
                        <span className="text-xs font-black uppercase text-slate-300 block mb-1">Simulate Image Drag-Drop</span>
                        <p className="text-[10px] text-slate-500 font-semibold leading-relaxed max-w-[280px] mx-auto mb-4">
                          Select one of our virtual image templates to run the offline compression scheduler.
                        </p>
                        
                        <button
                          onClick={startSimulateCompress}
                          disabled={simulateActive}
                          className="px-6 py-2.5 rounded-xl bg-white text-black hover:bg-slate-200 disabled:bg-white/10 disabled:text-slate-500 text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer"
                        >
                          {simulateActive ? 'Processing buffers...' : 'Run Local Compiler'}
                        </button>
                      </div>

                      {/* Hardware acceleration specifications */}
                      <div className="grid grid-cols-3 gap-3 font-mono text-[9px] font-black text-slate-500 text-center">
                        <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl space-y-0.5">
                          <span className="block text-slate-600">Thread Count:</span>
                          <span className="text-slate-300">4x Parallel</span>
                        </div>
                        <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl space-y-0.5">
                          <span className="block text-slate-600">Engine API:</span>
                          <span className="text-slate-300">WebGL & Canvas</span>
                        </div>
                        <div className="p-3 bg-white/[0.01] border border-white/5 rounded-xl space-y-0.5">
                          <span className="block text-[#aace16]">FPS Cap:</span>
                          <span className="text-[#aace16]">60.0 fps</span>
                        </div>
                      </div>
                    </div>

                    {/* Right: Results statistics */}
                    <div className="md:col-span-5 p-5 rounded-2xl bg-black/40 border border-white/5 space-y-5 h-full">
                      <h4 className="text-xs font-mono font-black uppercase text-slate-400 tracking-wider">COMPILER LOGS</h4>
                      
                      {simulateActive ? (
                        <div className="space-y-4 py-6 text-center">
                          <div className="relative w-12 h-12 rounded-full border-[3px] border-[#aace16]/10 border-t-[#aace16] animate-spin mx-auto" />
                          <div className="space-y-1">
                            <span className="text-xs font-bold text-slate-300 block">Compiling pixel matrix...</span>
                            <span className="text-[10px] text-slate-500 font-mono">{simulateProgress}% completed</span>
                          </div>
                        </div>
                      ) : simulatedSize ? (
                        <div className="space-y-4 animate-fade-in text-left">
                          <div className="p-3.5 rounded-xl bg-[#aace16]/10 border border-[#aace16]/20 text-[#aace16] text-xs font-black uppercase tracking-wider text-center">
                            🚀 Compression Successful!
                          </div>
                          
                          <div className="space-y-2.5 font-mono text-[10px] font-bold text-slate-400">
                            <div className="flex justify-between border-b border-white/5 pb-1.5">
                              <span>Saved payload:</span>
                              <span className="text-white">{simulatedSize}</span>
                            </div>
                            <div className="flex justify-between border-b border-white/5 pb-1.5">
                              <span>Hardware Time:</span>
                              <span className="text-emerald-400">{simulatedTime}</span>
                            </div>
                            <div className="flex justify-between border-b border-white/5 pb-1.5">
                              <span>Thread Pool:</span>
                              <span className="text-white">WebWorker Thread 1</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Security status:</span>
                              <span className="text-emerald-400 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Fully Local
                              </span>
                            </div>
                          </div>

                          <div className="pt-2 text-[10px] text-slate-500 font-semibold leading-relaxed">
                            Upgrade to Creative Pro to save optimized results directly to your local file explorer.
                          </div>
                        </div>
                      ) : (
                        <div className="h-48 flex items-center justify-center text-center text-slate-600 font-semibold text-xs border border-dashed border-white/5 rounded-xl">
                          Select templates and click "Run Local Compiler" to analyze results.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    {/* Left: AI Prompt input simulator */}
                    <div className="md:col-span-7 space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-mono font-black uppercase text-slate-400 tracking-wider">AI Prompter Input</label>
                        <textarea
                          rows={3}
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="Describe your creative vision..."
                          className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-2xl text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#aace16] transition-all font-semibold resize-none"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className="text-[9px] font-mono font-bold text-slate-500">Model target: DreamSeed v4.2 Local weights</span>
                        
                        <button
                          onClick={startSimulateAI}
                          disabled={simulateActive || !aiPrompt.trim()}
                          className="px-6 py-2.5 bg-[#aace16] hover:bg-[#bdef2b] text-black text-[10px] font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {simulateActive ? 'Synthesizing seed...' : 'Compile Prompt'}
                          <Sparkles className="w-3.5 h-3.5 fill-black" />
                        </button>
                      </div>

                      {/* Engine logging feed */}
                      {aiLogs.length > 0 && (
                        <div className="p-4 rounded-2xl bg-black border border-white/5 space-y-1.5 font-mono text-[9px] text-slate-400 text-left min-h-[100px]">
                          <span className="text-[#aace16] font-black uppercase tracking-widest block mb-2">NEURAL PIPELINE TRACE:</span>
                          {aiLogs.map((log, i) => (
                            <div key={i} className="flex gap-2">
                              <span className="text-slate-600">{`>`}</span>
                              <span>{log}</span>
                            </div>
                          ))}
                          {simulateActive && (
                            <div className="text-slate-500 animate-pulse">Running iteration matrices ({simulateProgress}% completed)...</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: Output Artwork visualization */}
                    <div className="md:col-span-5 p-4 rounded-2xl bg-black/40 border border-white/5 h-64 relative overflow-hidden flex items-center justify-center">
                      {simulateActive ? (
                        <div className="text-center space-y-4">
                          <div className="w-10 h-10 rounded-full border-2 border-t-[#aace16] border-white/5 animate-spin mx-auto" />
                          <span className="text-[10px] font-mono font-bold text-slate-400">Synthesizing visual seed matrices...</span>
                        </div>
                      ) : aiArtwork ? (
                        <div className="absolute inset-0 group cursor-pointer">
                          <img 
                            src={aiArtwork} 
                            className="w-full h-full object-cover animate-fade-in" 
                            alt="Generated AI artwork mockup" 
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <span className="text-[10px] font-mono text-slate-400">Seed: 98127391</span>
                            <span className="text-xs font-black uppercase text-[#aace16] mt-0.5">DreamSeed v4.2 HD</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-slate-600 space-y-2">
                          <Sparkles className="w-8 h-8 text-slate-600 mx-auto" />
                          <span className="text-[10px] font-semibold block">Click "Compile Prompt" to start simulated artwork renders.</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Stat Ribbon */}
              <div className="border-t border-white/5 pt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-3 bg-white/[0.01] rounded-xl border border-white/5">
                  <span className="text-slate-500 text-[9px] font-mono font-bold uppercase tracking-wider block">Local Speed</span>
                  <span className="text-white text-xs font-black tracking-tight">3.8ms Average</span>
                </div>
                <div className="p-3 bg-white/[0.01] rounded-xl border border-white/5">
                  <span className="text-slate-500 text-[9px] font-mono font-bold uppercase tracking-wider block">Privacy Rating</span>
                  <span className="text-emerald-400 text-xs font-black tracking-tight flex items-center justify-center gap-1">
                    <Shield className="w-3.5 h-3.5 text-emerald-400" /> 100% Secure
                  </span>
                </div>
                <div className="p-3 bg-white/[0.01] rounded-xl border border-white/5">
                  <span className="text-slate-500 text-[9px] font-mono font-bold uppercase tracking-wider block">Bandwidth Saved</span>
                  <span className="text-white text-xs font-black tracking-tight">24.8 GB (91%)</span>
                </div>
                <div className="p-3 bg-white/[0.01] rounded-xl border border-white/5">
                  <span className="text-slate-500 text-[9px] font-mono font-bold uppercase tracking-wider block">System Engine</span>
                  <span className="text-[#aace16] text-xs font-black tracking-tight">GPU Accelerated</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <PageWrapper title="404 - Page Not Found" theme={theme} mainPaddingTop={mainPaddingTop} onBackToHome={onBackToHome}>
      <div className="text-center py-10 space-y-4">
        <p className="text-slate-400">The path you followed is not found or is currently under architectural review.</p>
        <button onClick={onBackToHome} className="px-5 py-2.5 bg-[#aace16] text-black font-bold rounded-xl text-xs">
          Return to Home View
        </button>
      </div>
    </PageWrapper>
  );
}
